// Import statements at the very top
import React, { useEffect, useState, useContext, useRef } from 'react';
import FocusTrap from 'focus-trap-react';
import './ToolPropertiesPanel.css';
import '../TemplatesModal/TemplatesModal.css';
import cursorDefault from '../../assets/cursors/cursor-default.png';
import cursorPointer from '../../assets/cursors/cursor-pointer.png';
import cursorText from '../../assets/cursors/cursor-text.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faPlus,
  faMinus,  
  faFileDownload,
  faFileUpload,
  faFileExport,
  faBold,
  faItalic,
  faAlignLeft,
  faAlignCenter,
  faAlignRight,
  faAlignJustify,
  faArrowsAltH,
  faArrowsAltV,
  faObjectGroup, 
  faObjectUngroup,
  faScissors, 
  faEraser,
  faSyncAlt,
  faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import HeaderImage from '../../assets/Images/Header.png';
import FacebookIcon from '../../assets/Images/icons/facebook.svg';
import YoutubeIcon from '../../assets/Images/icons/youtube.svg';
import ThreeDMovieThumbnail from '../../assets/Images/3D Movie-100.webp';
import ClayFolkThumbnail from '../../assets/Images/Clay Folk-100.webp';
import CrayonThumbnail from '../../assets/Images/Crayon-100.webp';
import DoodleThumbnail from '../../assets/Images/Doodle-100.webp';
import FairytaleThumbnail from '../../assets/Images/Fairytale-100.webp';
import FeltFriendsThumbnail from '../../assets/Images/Felt Friends-100.webp';
import KawaiiThumbnail from '../../assets/Images/Kawaii-100.webp';
import KidsCartoonThumbnail from '../../assets/Images/Kids Cartoon-100.webp';
import OutlineThumbnail from '../../assets/Images/Outline-100.webp';
import PaperCutoutThumbnail from '../../assets/Images/Paper Cutout-100.webp';
import PhotorealisticThumbnail from '../../assets/Images/Photorealistic-100.webp';
import StickerThumbnail from '../../assets/Images/Sticker-100.webp';
import StorybookThumbnail from '../../assets/Images/Storybook-100.webp';
import WatercolourThumbnail from '../../assets/Images/Watercolour-100.webp';
import { UserContext } from '../../context/UserContext';
import { faImages } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

// ✅ Now you can safely run executable code like this:
library.add(faAlignCenter, faArrowsAltH, faArrowsAltV, faAlignLeft, faAlignRight, faAlignJustify, faObjectGroup, faObjectUngroup, faBold, faItalic, faScissors, faEraser, faSyncAlt);

const EDUCATIONAL_FONTWARE_URL = 'https://www.educationalfontware.com';

const getYouTubeEmbedUrl = (url, autoplay = true) => {
  if (!url) return '';

  try {
    const parsedUrl = new URL(url);
    const host = parsedUrl.hostname.replace(/^www\./, '');
    let videoId = '';

    if (host === 'youtu.be') {
      videoId = parsedUrl.pathname.split('/').filter(Boolean)[0] || '';
    } else if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsedUrl.pathname === '/watch') {
        videoId = parsedUrl.searchParams.get('v') || '';
      } else {
        const parts = parsedUrl.pathname.split('/').filter(Boolean);
        if (['shorts', 'embed', 'live'].includes(parts[0])) {
          videoId = parts[1] || '';
        }
      }
    }

    return /^[\w-]{6,}$/.test(videoId)
      ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplay ? '1' : '0'}&rel=0`
      : '';
  } catch (error) {
    return '';
  }
};

const EDUCATIONAL_FONTWARE_FONT_FAMILIES = [
  'DNManuscript',
  'DNManuscriptOutlines',
  'OZManuscript',
  'OZManuscriptOutlines',
  'HWTManuscript',
  'HWTManuscriptOutlines',
];

const isEducationalFontwareFont = (fontFamily) => {
  if (!fontFamily) return false;

  return EDUCATIONAL_FONTWARE_FONT_FAMILIES.includes(fontFamily);
};

const FONT_SAMPLE_TEXT = 'Aa Bb Cc 123';

const FONT_OPTIONS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },

  { value: 'edugroove-hand', label: 'EDUGroove Hand' },
  { value: 'edugroove-hand-arrows', label: 'EDUGroove Hand Arrows' },
  { value: 'edugroove-dots', label: 'EDUGroove Dots' },
  { value: 'edugroove-happy', label: 'EDUGroove Happy' },

  { value: 'DNManuscript', label: 'DN Manuscript' },
  { value: 'DNManuscriptOutlines', label: 'DN Manuscript Outlines' },
  { value: 'HWTManuscript', label: 'HWT Manuscript' },
  { value: 'HWTManuscriptOutlines', label: 'HWT Manuscript Outlines' },
  { value: 'OZManuscript', label: 'OZ Manuscript' },
  { value: 'OZManuscriptOutlines', label: 'OZ Manuscript Outlines' },

  { value: 'LettersForLearners', label: 'Letters For Learners' },
  { value: 'LettersForLearnersDots', label: 'Letters For Learners Dots' },
  { value: 'LettersForLearnersLined', label: 'Letters For Learners Lined' },
  { value: 'LettersForLearnersLinedDots', label: 'Letters For Learners Lined Dots' },

  { value: 'EduAUVICWANTArrows-VariableFont_wght', label: 'Edu AUS Arrows' },
  { value: 'EduAUVICWANTDots-VariableFont_wght', label: 'Edu AUS Dots' },
  { value: 'EduAUVICWANTHand-VariableFont_wght', label: 'Edu AUS Hand' },

  { value: 'LondrinaOutline-Regular', label: 'Londrina Colouring' },
  { value: 'BungeeOutline-Regular', label: 'Bungee Colouring' },

  { value: 'ComicRelief-Regular', label: 'Comic Relief' },
  { value: 'ComingSoon-Regular', label: 'Coming Soon' },
  { value: 'PermanentMarker-Regular', label: 'Permanent Marker' },
  { value: 'LexendMega-VariableFont_wght', label: 'Lexend Mega' },
];

const getFontPreviewFamily = (fontFamily) => {
  return `"${fontFamily}", Arial, sans-serif`;
};

const shapeSVGs = {
  rect: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4" y="4" width="16" height="16" />
    </svg>
  ),
  roundedRect: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="3" y="3" width="18" height="18" rx="4" ry="4" />
    </svg>
  ),
  circle: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
  semiCircle: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M3 14a9 9 0 0 1 18 0Z" />
    </svg>
  ),
  triangle: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon points="12,2 22,20 2,20" />
    </svg>
  ),
  star: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon points="12,2 15,8.5 22,9.3 17,14 18.5,21 12,17.3 5.5,21 7,14 2,9.3 9,8.5" />
    </svg>
  ),
  pentagon: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon points="12,2 19,7 17,19 7,19 5,7" />
    </svg>
  ),
  hexagon: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon points="12,2 19,7 19,17 12,22 5,17 5,7" />
    </svg>
  ),
    octagon: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="#000"
      xmlns="http://www.w3.org/2000/svg"
    >
      <polygon points="7,2 17,2 22,7 22,17 17,22 7,22 2,17 2,7" />
    </svg>
  ),
  grid: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="3" y="3" width="18" height="18" stroke="#000" strokeWidth="2" />
      <line x1="9" y1="3" x2="9" y2="21" stroke="#000" strokeWidth="2" />
      <line x1="15" y1="3" x2="15" y2="21" stroke="#000" strokeWidth="2" />
      <line x1="3" y1="9" x2="21" y2="9" stroke="#000" strokeWidth="2" />
      <line x1="3" y1="15" x2="21" y2="15" stroke="#000" strokeWidth="2" />
    </svg>
  ),
      circleSegments: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="9" stroke="#000" strokeWidth="2" />
          <line x1="12" y1="12" x2="12" y2="3" stroke="#000" strokeWidth="2" />
          <line x1="12" y1="12" x2="20" y2="8" stroke="#000" strokeWidth="2" />
          <line x1="12" y1="12" x2="18" y2="18" stroke="#000" strokeWidth="2" />
          <line x1="12" y1="12" x2="6" y2="18" stroke="#000" strokeWidth="2" />
        </svg>
      ),
      maze: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3" y="3" width="18" height="18" stroke="#000" strokeWidth="2" />
          <line x1="3" y1="8" x2="10" y2="8" stroke="#000" strokeWidth="2" />
          <line x1="14" y1="8" x2="21" y2="8" stroke="#000" strokeWidth="2" />
          <line x1="8" y1="8" x2="8" y2="16" stroke="#000" strokeWidth="2" />
          <line x1="8" y1="16" x2="16" y2="16" stroke="#000" strokeWidth="2" />
          <line x1="16" y1="12" x2="16" y2="21" stroke="#000" strokeWidth="2" />
        </svg>
      ),
      wordSearch: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* A - top left */}
          <text
            x="2.5"
            y="8"
            fontSize="12"
            fontWeight="700"
            fill="#000"
            transform="rotate(-10 2.5 8)"
          >
            A
          </text>

          {/* B - bottom right */}
          <text
            x="11"
            y="21"
            fontSize="12"
            fontWeight="700"
            fill="#000"
            transform="rotate(8 11 21)"
          >
            B
          </text>

          {/* C - bottom left */}
          <text
            x="2.5"
            y="21"
            fontSize="12"
            fontWeight="700"
            fill="#000"
            transform="rotate(-6 2.5 21)"
          >
            C
          </text>

          {/* Magnifying glass */}
          <circle cx="17" cy="9" r="4" stroke="#000" strokeWidth="2" />
          <line
            x1="19.5"
            y1="11.5"
            x2="22"
            y2="14"
            stroke="#000"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
};

const ToolPropertiesPanel = ({
  isPropertiesCollapsed,
  togglePropertiesPanel,
  selectedShapeIds,
  allSelectedAreText,
  allSelectedAreShapes,
  allSelectedAreLines,
  allSelectedArePencilLines, // Newly added prop
  allSelectedArePenShapes,
  shapeProperties,
  updateShapePropertiesHandler,
  setShapeProperties,
  textProperties,
  updateTextPropertiesHandler,
  selectedTool,
  currentTemplate = null,
  setSelectedShapeType,
  selectedShapeType,
  changeCanvasOrientation,
  pages,
  activePage,
  changeMood,
  handleZoomIn,
  handleZoomOut,
  updateLinePropertiesHandler,
  lineProperties,
  updatePencilPropertiesHandler,
  pencilProperties,
  addBlankPage,
  duplicateCurrentPage,  
  exportToPDF,
  openAdvancedPDFExport,
  triggerFileInput,
  triggerPDFInput,
  triggerImageUpload,
  saveStateAsJSON,
  isGridVisible,
  setIsGridVisible,
  isQuartersGridVisible,
  setIsQuartersGridVisible,
  isThirdsGridVisible,
  setIsThirdsGridVisible,
  isRulerGridVisible,
  setIsRulerGridVisible,

  isMeasurementRulerVisible,
  setIsMeasurementRulerVisible,
  isMeasurementRulerSelected,
  setIsMeasurementRulerSelected,
  measurementRulerSettings,
  setMeasurementRulerSettings,
  setIsCustomGridVisible,
  isCustomGridVisible,
  
  customGridSettings,
  setCustomGridSettings,
  exportFilename,
  setExportFilename,
  allSelectedAreImages,
  setShowImageLibraryModal,
  onSwapImage,
  onSetSquareLogo,
  onSetLogo,
  onUploadTemplateClick,  
  setPages,
  setSelectedShapeIds,
  setSelectedTool,  
  alignSelectionVertically,
  alignSelectionHorizontally,
  distributeSelectionHorizontally,
  distributeSelectionVertically,
  groupSelectedShapes,
  ungroupSelectedShapes,
  flipSelectedHorizontally,
  flipSelectedVertically,
  penToolSettings,
  setPenToolSettings,  
  isGroupSelection,
  onApplyPenMask,
  onReleasePenMask,
  pushToUndoStack,
  recordPropertyChangeForUndo,
  regenerateSelectedMaze,  
  bucketSettings,
  setBucketSettings,
}) => {
  const [isTemplateVideoOpen, setIsTemplateVideoOpen] = useState(false);
  const templateVideoEmbedUrl = getYouTubeEmbedUrl(currentTemplate?.videoUrl);
  const templateVideoInlineUrl = getYouTubeEmbedUrl(
    currentTemplate?.videoUrl,
    false
  );
  const showTemplateTutorial =
    ['select', 'pointer', 'hand', 'pan'].includes(selectedTool) &&
    Boolean(templateVideoEmbedUrl);

  useEffect(() => {
    if (!isTemplateVideoOpen) return undefined;

    const handleVideoEscape = (event) => {
      if (event.key === 'Escape') setIsTemplateVideoOpen(false);
    };

    window.addEventListener('keydown', handleVideoEscape);
    return () => window.removeEventListener('keydown', handleVideoEscape);
  }, [isTemplateVideoOpen]);

  useEffect(() => {
    setIsTemplateVideoOpen(false);
  }, [currentTemplate?.file, currentTemplate?.videoUrl]);

  // Optional: Debugging to verify the shape type
  useEffect(() => {
    console.log('Selected Shape Type:', shapeProperties.type);
  }, [shapeProperties.type]);

 
  useEffect(() => {
    if (!selectedShape?.meta?.tracingType) return;
  
    regenerateTracingLine(
      selectedShape,
      selectedShape.meta.tracingType,
      selectedShape.meta.tracingPoints,
      selectedShape.meta.tracingLength
    );
  }, [
    lineProperties.strokeWidth,
    lineProperties.strokeColor,
    lineProperties.dashEnabled,
    lineProperties.dash,
  ]);
  
  
  const selectedShape = pages[activePage]?.shapes.find(
    (shape) => shape.id.toString() === selectedShapeIds[0]
  );

  const [isArcShiftPressed, setIsArcShiftPressed] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        setIsArcShiftPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        setIsArcShiftPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (selectedTool !== 'shape') return;
    if (selectedShapeIds.length > 0) return;
    if (selectedShapeType === 'rect') return;

    setShapeProperties((prev) => {
      if (
        prev.strokeStyle === 'solid' &&
        prev.dashEnabled === false &&
        Array.isArray(prev.dash) &&
        prev.dash[0] === 10 &&
        prev.dash[1] === 5
      ) {
        return prev;
      }

      return {
        ...prev,
        strokeStyle: 'solid',
        dashEnabled: false,
        dash: [10, 5],
        strokeWaveSize: 8,
        strokeWaveFrequency: 6,
      };
    });
  }, [selectedTool, selectedShapeType, selectedShapeIds.length, setShapeProperties]);

  useEffect(() => {
    if (!selectedShape) return;
    console.log("Selected image meta:", selectedShape.meta);
  }, [selectedShape]);

  const isTracingLine = selectedShape?.meta?.tracingType;
  const showPenSettings = selectedTool === 'pen' && selectedShapeIds.length === 0;
  const showPenShapeSettings = selectedShape?.type === 'pen' || allSelectedArePenShapes;
  const selectedBezierPointIndex =
    selectedShape?.type === 'pen' &&
    selectedShape?.bezierEnabled &&
    Number.isInteger(selectedShape?.bezierSelectedPointIndex)
      ? selectedShape.bezierSelectedPointIndex
      : null;

  const updateSelectedBezierShape = (updater) => {
    if (!selectedShape || selectedShape.type !== 'pen' || !selectedShape.bezierEnabled) return;

    pushToUndoStack();

    setPages((prevPages) =>
      prevPages.map((page, pageIndex) => {
        if (pageIndex !== activePage) return page;

        return {
          ...page,
          shapes: page.shapes.map((shape) => {
            if (shape.id.toString() !== selectedShape.id.toString()) return shape;

            const updatedShape = updater(shape);

            return {
              ...updatedShape,
              points: Array.isArray(updatedShape.bezierPoints)
                ? updatedShape.bezierPoints.flatMap((point) => [point.x, point.y])
                : updatedShape.points,
            };
          }),
        };
      })
    );
  };

  const getDefaultBezierHandlesForPoint = (shape, pointIndex) => {
    const points = shape.bezierPoints || [];
    const point = points[pointIndex];

    if (!point || points.length < 2) {
      return {
        in: { x: 0, y: 0 },
        out: { x: 0, y: 0 },
      };
    }

    const isClosed = !!shape.closed;
    const isFirstPoint = pointIndex === 0;
    const isLastPoint = pointIndex === points.length - 1;

    const prevPoint = isFirstPoint
      ? (isClosed ? points[points.length - 1] : point)
      : points[pointIndex - 1];

    const nextPoint = isLastPoint
      ? (isClosed ? points[0] : point)
      : points[pointIndex + 1];

    const dx = nextPoint.x - prevPoint.x;
    const dy = nextPoint.y - prevPoint.y;
    const length = Math.max(1, Math.hypot(dx, dy));

    const neighbourDistance = Math.max(
      Math.hypot(point.x - prevPoint.x, point.y - prevPoint.y),
      Math.hypot(nextPoint.x - point.x, nextPoint.y - point.y),
      1
    );

    const handleLength = Math.min(35, Math.max(12, neighbourDistance * 0.25));

    const nextHandles = {
      in: {
        x: (-dx / length) * handleLength,
        y: (-dy / length) * handleLength,
      },
      out: {
        x: (dx / length) * handleLength,
        y: (dy / length) * handleLength,
      },
    };

    // For an open path, the first point should not have an incoming handle.
    if (!isClosed && isFirstPoint) {
      nextHandles.in = { x: 0, y: 0 };
    }

    // For an open path, the last point should not have an outgoing handle.
    if (!isClosed && isLastPoint) {
      nextHandles.out = { x: 0, y: 0 };
    }

    return nextHandles;
  };

  const setSelectedBezierPointMode = (mode) => {
    if (selectedBezierPointIndex === null) return;

    updateSelectedBezierShape((shape) => {
      const nextBezierPoints = [...shape.bezierPoints];
      const point = nextBezierPoints[selectedBezierPointIndex];

      if (!point) return shape;

      if (mode === 'corner') {
        nextBezierPoints[selectedBezierPointIndex] = {
          ...point,
          in: { x: 0, y: 0 },
          out: { x: 0, y: 0 },
          pointMode: 'corner',
        };
      }

      if (mode === 'smooth') {
        const handles = getDefaultBezierHandlesForPoint(shape, selectedBezierPointIndex);

        nextBezierPoints[selectedBezierPointIndex] = {
          ...point,
          ...handles,
          pointMode: 'smooth',
        };
      }

      return {
        ...shape,
        bezierPoints: nextBezierPoints,
      };
    });
  };

  const resetSelectedBezierPoint = () => {
    setSelectedBezierPointMode('smooth');
  };

  const resetAllBezierPoints = () => {
    updateSelectedBezierShape((shape) => {
      const nextBezierPoints = shape.bezierPoints.map((point, index) => {
        const handles = getDefaultBezierHandlesForPoint(shape, index);

        return {
          ...point,
          ...handles,
          pointMode: 'smooth',
        };
      });

      return {
        ...shape,
        bezierPoints: nextBezierPoints,
        bezierSelectedPointIndex: selectedBezierPointIndex,
      };
    });
  };

  const toggleSelectedBezierSymmetry = (checked) => {
    updateSelectedBezierShape((shape) => ({
      ...shape,
      bezierSymmetricalHandles: checked,
    }));
  };
  const height = selectedShape?.meta?.tracingHeight || 50;
  const allStrokeEnabled = shapeProperties.strokeEnabled;
  const allFillEnabled = shapeProperties.fillEnabled;
  const logoSrc = `${process.env.PUBLIC_URL}/assets/Images/Logo.png`;  
  const { currentUser } = useContext(UserContext);
  const isBasic = currentUser?.planRole === 'armember_basic';
  const onlyPencilOrLineSelected = (allSelectedArePencilLines || allSelectedAreLines);
  const [imagePreserveWhiteFill, setImagePreserveWhiteFill] = useState(true);
  const [imageFillColor, setImageFillColor] = useState("#ffffff");
  const [imageStrokeColor, setImageStrokeColor] = useState("#000000");
  const [imageFillStyle, setImageFillStyle] = useState('solid');
  const [imageFillSecondaryColor, setImageFillSecondaryColor] = useState('#F2D6A7');
  const [imageFillPatternSize, setImageFillPatternSize] = useState(10);
  const [imageFillPatternSpacing, setImageFillPatternSpacing] = useState(8);
  const [imageFillPatternRotation, setImageFillPatternRotation] = useState(0);
  const saveFilenameInputRef = useRef(null);
  const fontPickerRef = useRef(null);
  const [openFontPickerKey, setOpenFontPickerKey] = useState(null);

  const panelCursorVars = {
    '--panel-cursor-default': `url(${cursorDefault}) 2 2, default`,
    '--panel-cursor-pointer': `url(${cursorPointer}) 6 2, pointer`,
    '--panel-cursor-text': `url(${cursorText}) 0 0, text`,
  };

  useEffect(() => {
    const handleClickOutsideFontPicker = (event) => {
      if (
        fontPickerRef.current &&
        !fontPickerRef.current.contains(event.target)
      ) {
        setOpenFontPickerKey(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideFontPicker);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFontPicker);
    };
  }, []);

  const renderFontFamilyPicker = ({
    value,
    onChange,
    pickerKey = 'text-font-picker',
  }) => {
    const selectedFont =
      FONT_OPTIONS.find((font) => font.value === value) || FONT_OPTIONS[0];

    const isOpen = openFontPickerKey === pickerKey;

    return (
      <div className="font-preview-picker" ref={fontPickerRef}>
        <button
          type="button"
          className={`font-preview-picker-button ${isOpen ? 'open' : ''}`}
          onClick={() =>
            setOpenFontPickerKey((currentKey) =>
              currentKey === pickerKey ? null : pickerKey
            )
          }
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="font-preview-picker-selected">
            {selectedFont.label}
          </span>

          <span
            className="font-preview-picker-selected-sample"
            style={{ fontFamily: getFontPreviewFamily(selectedFont.value) }}
          >
            {FONT_SAMPLE_TEXT}
          </span>

          <span className="font-preview-picker-arrow">
            ▾
          </span>
        </button>

        {isOpen && (
          <div className="font-preview-picker-menu" role="listbox">
            {FONT_OPTIONS.map((font) => {
              const isSelected = font.value === value;

              return (
                <button
                  key={font.value}
                  type="button"
                  className={`font-preview-picker-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    onChange({ target: { value: font.value } });
                    setOpenFontPickerKey(null);
                  }}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="font-preview-picker-name">
                    {font.label}
                  </span>

                  <span
                    className="font-preview-picker-sample"
                    style={{ fontFamily: getFontPreviewFamily(font.value) }}
                  >
                    {FONT_SAMPLE_TEXT}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderEducationalFontwareCredit = (fontFamily) => {
  if (!isEducationalFontwareFont(fontFamily)) return null;

  return (
    <div className="efi-font-credit">
      <div className="efi-font-credit-title">
        ⓘ Educational Fontware font
      </div>

      <div className="efi-font-credit-text">
        Handwriting font provided by{' '}
        <a
          href={EDUCATIONAL_FONTWARE_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Educational Fontware, Inc.
        </a>
        . More variations: Rules, Dots, Arrows, phonics, and more.
      </div>
    </div>
  );
};

  const commonColours = [
  '#000000',
  '#ffffff',
  '#ff0000',
  '#ff9900',
  '#ffff00',
  '#00cc00',
  '#00bcd4',
  '#0000ff',
  '#8000ff',
  '#ff00ff',
  '#ffb6c1',
  '#a52a2a',
];

const renderColourSwatches = (currentColour, onSelectColour) => (
  <div className="compact-colour-grid">
    {commonColours.map((colour) => (
      <button
        key={colour}
        type="button"
        className={`bucket-colour-swatch ${
          currentColour?.toLowerCase() === colour.toLowerCase() ? 'active' : ''
        }`}
        style={{ backgroundColor: colour }}
        onClick={() => onSelectColour(colour)}
        title={colour}
        aria-label={`Select ${colour}`}
      />
    ))}
  </div>
);

  const renderTwoValueDashInputs = (dash = [10, 5], onChangeDash) => {
    const safeDash = Array.isArray(dash) && dash.length >= 2 ? dash : [10, 5];

    return (
      <div className="stroke-options-row">
        <h3 className="stroke-options-label">Dash:</h3>

        <input
          type="number"
          min="0"
          value={safeDash[0] ?? 10}
          onChange={(e) => {
            const first = parseInt(e.target.value, 10) || 0;
            const second = safeDash[1] ?? 5;
            onChangeDash([first, second]);
          }}
          className="number-input stroke-options-input"
        />

        <input
          type="number"
          min="0"
          value={safeDash[1] ?? 5}
          onChange={(e) => {
            const first = safeDash[0] ?? 10;
            const second = parseInt(e.target.value, 10) || 0;
            onChangeDash([first, second]);
          }}
          className="number-input stroke-options-input"
        />
      </div>
    );
  };

  const renderDashToggleCard = ({
    checked,
    onToggle,
    dash,
    onChangeDash,
  }) => {
    return (
      <div className="property-group dash-toggle-card">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={checked}
            onChange={onToggle}
          />
          Dashed Line
        </label>

        {checked && renderTwoValueDashInputs(dash, onChangeDash)}
      </div>
    );
  };

  const renderBasicShapeDashControls = () => {
  const applyBasicStrokeStyle = (style) => {
    const nextDash =
      style === 'dashed'
        ? [10, 5]
        : style === 'dotted'
          ? [0, Math.max(4, (shapeProperties.strokeWidth || 2) * 2.5)]
          : [];

    setShapeProperties((prev) => ({
      ...prev,
      strokeStyle: style,
      dashEnabled: style === 'dashed' || style === 'dotted',
      dash: nextDash,
      strokeWaveSize: 8,
      strokeWaveFrequency: 6,
    }));

    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(
        { target: { value: style } },
        'strokeStyle'
      );
    }
  };

  const updateDashPattern = (nextDash) => {
    setShapeProperties((prev) => ({
      ...prev,
      strokeStyle: shapeProperties.strokeStyle || 'dashed',
      dash: nextDash,
      dashEnabled: true,
      strokeWaveSize: 8,
      strokeWaveFrequency: 6,
    }));

    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(
        { target: { value: nextDash.join(',') } },
        'dash'
      );
    }
  };

  return (
    <>
      <div className="property-group">
        <h3 className="property-heading">Stroke Style:</h3>
        <select
          value={shapeProperties.strokeStyle || 'solid'}
          onChange={(e) => applyBasicStrokeStyle(e.target.value)}
          className="select-input"
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
        </select>
      </div>

      {['dashed', 'dotted'].includes(shapeProperties.strokeStyle) &&
        renderTwoValueDashInputs(
          Array.isArray(shapeProperties.dash) && shapeProperties.dash.length >= 2
            ? shapeProperties.dash
            : [10, 5],
          updateDashPattern
        )}
    </>
  );
};

  const renderShapeStrokeStyleControls = () => {
    const applyStrokeStyle = (style) => {
      const nextDash =
        style === 'dashed'
          ? [10, 5]
          : style === 'dotted'
            ? [0, Math.max(4, (shapeProperties.strokeWidth || 2) * 2.5)]
            : [];

      setShapeProperties((prev) => ({
        ...prev,
        strokeStyle: style,
        dashEnabled: style === 'dashed' || style === 'dotted',
        dash: nextDash,
      }));

      if (selectedShapeIds.length > 0) {
        updateShapePropertiesHandler({ target: { value: style } }, 'strokeStyle');
      }
    };

    const updateDashPattern = (rawValue) => {
      const value = rawValue
        .split(',')
        .map((val) => parseInt(val.trim(), 10))
        .filter((val) => !isNaN(val) && val >= 0);

      setShapeProperties((prev) => ({
        ...prev,
        dash: value,
        dashEnabled: true,
      }));

      if (selectedShapeIds.length > 0) {
        updateShapePropertiesHandler({ target: { value: value.join(',') } }, 'dash');
      }
    };      

  return (
    <>
      <div className="property-group">
        <h3 className="property-heading">Stroke Style:</h3>
        <select
          value={shapeProperties.strokeStyle || 'solid'}
          onChange={(e) => applyStrokeStyle(e.target.value)}
          className="select-input"
        >
          <option value="solid">Solid</option>
          <option value="dashed">Dashed</option>
          <option value="dotted">Dotted</option>
          <option value="zigzag">Zig Zag</option>
          <option value="wavy">Wavy</option>
        </select>
      </div>

      {['dashed', 'dotted'].includes(shapeProperties.strokeStyle) && (
        <div className="stroke-options-row">
          <h3 className="stroke-options-label">Dash:</h3>

          <input
            type="number"
            min="0"
            value={(shapeProperties.dash || [10, 5])[0] ?? 10}
            onChange={(e) => {
              const first = parseInt(e.target.value, 10) || 0;
              const second = (shapeProperties.dash || [10, 5])[1] ?? 5;
              updateDashPattern(`${first},${second}`);
            }}
            className="number-input stroke-options-input"
          />

          <input
            type="number"
            min="0"
            value={(shapeProperties.dash || [10, 5])[1] ?? 5}
            onChange={(e) => {
              const first = (shapeProperties.dash || [10, 5])[0] ?? 10;
              const second = parseInt(e.target.value, 10) || 0;
              updateDashPattern(`${first},${second}`);
            }}
            className="number-input stroke-options-input"
          />
        </div>
      )}

      {['zigzag', 'wavy'].includes(shapeProperties.strokeStyle) && (
        <div className="stroke-options-row">
          <h3 className="stroke-options-label">Size:</h3>

          <input
            type="number"
            min="1"
            max="50"
            value={shapeProperties.strokeWaveSize || 8}
            onChange={(e) => {
              const value = parseFloat(e.target.value);

              setShapeProperties((prev) => ({
                ...prev,
                strokeWaveSize: value,
              }));

              if (selectedShapeIds.length > 0) {
                updateShapePropertiesHandler({ target: { value } }, 'strokeWaveSize');
              }
            }}
            className="number-input stroke-options-input"
          />

          <h3 className="stroke-options-label">Waves:</h3>

          <input
            type="number"
            min="1"
            max="40"
            value={shapeProperties.strokeWaveFrequency || 6}
            onChange={(e) => {
              const value = parseFloat(e.target.value);

              setShapeProperties((prev) => ({
                ...prev,
                strokeWaveFrequency: value,
              }));

              if (selectedShapeIds.length > 0) {
                updateShapePropertiesHandler({ target: { value } }, 'strokeWaveFrequency');
              }
            }}
            className="number-input stroke-options-input"
          />
        </div>
      )}
    </>
  );
};
  
  const trackLoginAction = async (action) => {
    try {
      const logId = localStorage.getItem('currentLoginLogId');
      if (!logId) return;

      await axios.post('https://edugroove-backend.onrender.com/api/users/log-action', {
        logId,
        action,
      });
    } catch (err) {
      console.error('Failed to track login action:', err);
    }
  };

  const handleTrackedExportToPDF = async () => {
    await trackLoginAction('exportPdf');
    exportToPDF();
  };

  const handleOpenAdvancedPDFExport = () => {
    if (openAdvancedPDFExport) {
      openAdvancedPDFExport();
    }
  };

  const handleTrackedSaveStateAsJSON = async () => {
    await trackLoginAction('downloadGroove');
    saveStateAsJSON();
  };

  // Detect if the currently selected image is an editable SVG image
  const selectedImageSrc = selectedShape?.src || '';

  const selectedIsLegacyEduGrooveSvg =
    typeof selectedImageSrc === 'string' &&
    (
      selectedImageSrc.includes('/Icons/') ||
      selectedImageSrc.includes('/app/Icons/')
    ) &&
    selectedImageSrc.toLowerCase().endsWith('.svg');

  const selectedIsEditableSvgImage =
    allSelectedAreImages &&
    selectedShapeIds.length === 1 &&
    (
      !!selectedShape?.meta?.svgOriginal ||
      selectedIsLegacyEduGrooveSvg
    );

  // OpenMoji and Fluent Emoji Outline already have dedicated colour versions.
  // Keep their normal image controls, but do not show SVG recolouring/fill styles.
  // Checking all metadata values also covers older library records that used
  // different field names for the repository.
  const selectedImageLibraryIdentity = [
    typeof selectedImageSrc === 'string' && !selectedImageSrc.startsWith('data:')
      ? selectedImageSrc
      : '',
    ...Object.entries(selectedShape?.meta || {})
      .filter(([, value]) =>
        typeof value === 'string' &&
        !value.startsWith('data:') &&
        !value.trim().startsWith('<svg')
      )
      .map(([, value]) => value),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const selectedIsOpenMoji =
    /open[\s_-]*moji/.test(selectedImageLibraryIdentity);

  const selectedIsFluentEmoji =
    /fluent[\s_-]*(emoji)?/.test(selectedImageLibraryIdentity);

  const selectedIsEmojiLibrary = selectedIsOpenMoji || selectedIsFluentEmoji;

  const selectedIsEmojiColourVariant =
    selectedIsEmojiLibrary &&
    /(colour|color|coloured|colored|full[\s_-]*colour|full[\s_-]*color)/.test(
      selectedImageLibraryIdentity
    );

  const selectedSupportsSvgFillEditing =
    selectedIsEditableSvgImage && !selectedIsEmojiLibrary;

  const selectedSupportsSvgOutlineEditing =
    selectedIsEditableSvgImage &&
    (!selectedIsEmojiLibrary || !selectedIsEmojiColourVariant);

  useEffect(() => {
    if (!selectedIsEditableSvgImage) return;

    setImagePreserveWhiteFill(
      selectedShape?.meta?.preserveWhiteFill ?? true
    );
    setImageFillColor(
      selectedShape?.meta?.fillColor ?? "#ffffff"
    );
    setImageStrokeColor(
      selectedShape?.meta?.strokeColor ?? "#000000"
    );
    setImageFillStyle(selectedShape?.meta?.fillStyle ?? 'solid');
    setImageFillSecondaryColor(selectedShape?.meta?.fillSecondaryColor ?? '#F2D6A7');
    setImageFillPatternSize(selectedShape?.meta?.fillPatternSize ?? 10);
    setImageFillPatternSpacing(selectedShape?.meta?.fillPatternSpacing ?? 8);
    setImageFillPatternRotation(selectedShape?.meta?.fillPatternRotation ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShapeIds, activePage, selectedIsEditableSvgImage]);
  
    
  // Handlers for SVG Fill Data
  const buildSvgDataUrl = (
    svgText,
    preserveWhiteFill,
    fillColor,
    strokeColor,
    fillStyle = 'solid',
    fillSecondaryColor = '#F2D6A7',
    fillPatternSize = 10,
    fillPatternSpacing = 8,
    fillPatternRotation = 0,
    renderedWidth = 0,
    renderedHeight = 0
  ) => {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
      const svgEl = svgDoc.documentElement;

      if (!svgEl.getAttribute("xmlns")) {
        svgEl.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      }

      const elements = [svgEl, ...svgDoc.querySelectorAll("*")];
      const styleTags = svgDoc.querySelectorAll("style");
      const svgNamespace = 'http://www.w3.org/2000/svg';

      const createSvgElement = (name, attributes = {}) => {
        const element = svgDoc.createElementNS(svgNamespace, name);
        Object.entries(attributes).forEach(([key, value]) => {
          element.setAttribute(key, String(value));
        });
        return element;
      };

      const getSvgBounds = () => {
        const viewBox = (svgEl.getAttribute('viewBox') || '')
          .trim()
          .split(/[\s,]+/)
          .map(Number);

        if (viewBox.length === 4 && viewBox.every(Number.isFinite)) {
          return { x: viewBox[0], y: viewBox[1], width: Math.max(1, viewBox[2]), height: Math.max(1, viewBox[3]) };
        }

        const width = parseFloat(svgEl.getAttribute('width')) || 100;
        const height = parseFloat(svgEl.getAttribute('height')) || 100;
        return { x: 0, y: 0, width, height };
      };

      const buildSvgFillPaint = () => {
        if (!fillStyle || fillStyle === 'solid') return fillColor;

        let defs = svgEl.querySelector('defs');
        if (!defs) {
          defs = createSvgElement('defs');
          svgEl.insertBefore(defs, svgEl.firstChild);
        }

        const oldDefinition = svgEl.querySelector('#edugroove-svg-fill');
        if (oldDefinition) oldDefinition.remove();

        const bounds = getSvgBounds();
        const gradientStyles = ['horizontalGradient', 'verticalGradient', 'diagonalGradient'];

        if (gradientStyles.includes(fillStyle)) {
          const coordinates = fillStyle === 'horizontalGradient'
            ? { x1: bounds.x, y1: bounds.y, x2: bounds.x + bounds.width, y2: bounds.y }
            : fillStyle === 'verticalGradient'
              ? { x1: bounds.x, y1: bounds.y, x2: bounds.x, y2: bounds.y + bounds.height }
              : { x1: bounds.x, y1: bounds.y, x2: bounds.x + bounds.width, y2: bounds.y + bounds.height };
          const gradient = createSvgElement('linearGradient', {
            id: 'edugroove-svg-fill',
            gradientUnits: 'userSpaceOnUse',
            ...coordinates,
          });
          gradient.appendChild(createSvgElement('stop', { offset: '0%', 'stop-color': fillColor }));
          gradient.appendChild(createSvgElement('stop', { offset: '100%', 'stop-color': fillSecondaryColor }));
          defs.appendChild(gradient);
          return 'url(#edugroove-svg-fill)';
        }

        if (fillStyle === 'radialGradient') {
          const gradient = createSvgElement('radialGradient', {
            id: 'edugroove-svg-fill',
            gradientUnits: 'userSpaceOnUse',
            cx: bounds.x + bounds.width / 2,
            cy: bounds.y + bounds.height / 2,
            r: Math.max(bounds.width, bounds.height) / 2,
          });
          gradient.appendChild(createSvgElement('stop', { offset: '0%', 'stop-color': fillColor }));
          gradient.appendChild(createSvgElement('stop', { offset: '100%', 'stop-color': fillSecondaryColor }));
          defs.appendChild(gradient);
          return 'url(#edugroove-svg-fill)';
        }

        // Pattern controls are expressed in visible canvas pixels. SVG artwork
        // often uses a tiny viewBox (for example 24 x 24), so using those values
        // directly makes its dots and symbols appear enormous. Convert the
        // visible size into this SVG's internal coordinate system.
        const horizontalUnitScale = renderedWidth > 0
          ? bounds.width / renderedWidth
          : 1;
        const verticalUnitScale = renderedHeight > 0
          ? bounds.height / renderedHeight
          : horizontalUnitScale;
        const svgUnitsPerCanvasPixel = Math.sqrt(
          Math.max(0.000001, horizontalUnitScale * verticalUnitScale)
        );
        const requestedSize = Math.max(3, Math.min(40, Number(fillPatternSize) || 10));
        const requestedSpacing = Math.max(0, Math.min(40, Number(fillPatternSpacing) || 0));
        const size = requestedSize * svgUnitsPerCanvasPixel;
        const spacing = requestedSpacing * svgUnitsPerCanvasPixel;
        const tile = Math.max(svgUnitsPerCanvasPixel, size + spacing);
        const centre = tile / 2;
        const radius = Math.min(size / 2, tile * 0.42);
        const strokeWidth = Math.max(svgUnitsPerCanvasPixel, size * 0.18);
        const pattern = createSvgElement('pattern', {
          id: 'edugroove-svg-fill',
          patternUnits: 'userSpaceOnUse',
          width: tile,
          height: tile,
          patternTransform: `rotate(${Number(fillPatternRotation) || 0} ${centre} ${centre})`,
        });
        pattern.appendChild(createSvgElement('rect', { width: tile, height: tile, fill: fillColor }));

        const paint = { fill: fillSecondaryColor, stroke: fillSecondaryColor, 'stroke-width': strokeWidth, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' };
        if (fillStyle === 'dots') {
          pattern.appendChild(createSvgElement('circle', { cx: centre, cy: centre, r: radius, fill: fillSecondaryColor }));
        } else if (fillStyle === 'squares') {
          pattern.appendChild(createSvgElement('rect', { x: centre - radius, y: centre - radius, width: radius * 2, height: radius * 2, fill: fillSecondaryColor }));
        } else if (fillStyle === 'hearts') {
          const s = radius * 1.7;
          pattern.appendChild(createSvgElement('path', {
            d: `M ${centre} ${centre + s * 0.65} C ${centre - s} ${centre}, ${centre - s * 0.55} ${centre - s * 0.65}, ${centre} ${centre - s * 0.18} C ${centre + s * 0.55} ${centre - s * 0.65}, ${centre + s} ${centre}, ${centre} ${centre + s * 0.65} Z`,
            fill: fillSecondaryColor,
          }));
        } else if (fillStyle === 'stars') {
          const points = Array.from({ length: 10 }, (_, index) => {
            const angle = -Math.PI / 2 + (index * Math.PI) / 5;
            const pointRadius = index % 2 === 0 ? radius : radius * 0.45;
            return `${centre + Math.cos(angle) * pointRadius},${centre + Math.sin(angle) * pointRadius}`;
          }).join(' ');
          pattern.appendChild(createSvgElement('polygon', { points, fill: fillSecondaryColor }));
        } else if (fillStyle === 'checks') {
          pattern.appendChild(createSvgElement('rect', { width: centre, height: centre, fill: fillSecondaryColor }));
          pattern.appendChild(createSvgElement('rect', { x: centre, y: centre, width: centre, height: centre, fill: fillSecondaryColor }));
        } else if (fillStyle === 'lines') {
          pattern.appendChild(createSvgElement('path', { d: `M 0 ${centre} H ${tile}`, ...paint, fill: 'none' }));
        } else if (fillStyle === 'diagonalLines') {
          pattern.appendChild(createSvgElement('path', { d: `M ${-tile * 0.25} ${tile} L ${tile * 0.75} 0 M ${tile * 0.25} ${tile} L ${tile * 1.25} 0`, ...paint, fill: 'none' }));
        } else if (fillStyle === 'grid') {
          pattern.appendChild(createSvgElement('path', { d: `M 0 0 H ${tile} M 0 0 V ${tile}`, ...paint, fill: 'none' }));
        } else if (fillStyle === 'crosshatch') {
          pattern.appendChild(createSvgElement('path', { d: `M 0 0 L ${tile} ${tile} M ${tile} 0 L 0 ${tile}`, ...paint, fill: 'none' }));
        } else if (fillStyle === 'waves') {
          pattern.appendChild(createSvgElement('path', { d: `M 0 ${centre} C ${tile * 0.25} ${centre - radius}, ${tile * 0.25} ${centre + radius}, ${tile * 0.5} ${centre} C ${tile * 0.75} ${centre - radius}, ${tile * 0.75} ${centre + radius}, ${tile} ${centre}`, ...paint, fill: 'none' }));
        } else if (fillStyle === 'zigzags') {
          pattern.appendChild(createSvgElement('path', { d: `M 0 ${centre + radius} L ${centre} ${centre - radius} L ${tile} ${centre + radius}`, ...paint, fill: 'none' }));
        } else if (fillStyle === 'confetti') {
          pattern.appendChild(createSvgElement('path', { d: `M ${tile * 0.15} ${tile * 0.25} L ${tile * 0.35} ${tile * 0.12} M ${tile * 0.62} ${tile * 0.24} L ${tile * 0.75} ${tile * 0.42} M ${tile * 0.25} ${tile * 0.7} L ${tile * 0.42} ${tile * 0.82} M ${tile * 0.72} ${tile * 0.68} L ${tile * 0.88} ${tile * 0.58}`, ...paint, fill: 'none' }));
        }

        defs.appendChild(pattern);
        return 'url(#edugroove-svg-fill)';
      };

      const normaliseColor = (value) => {
        if (!value) return "";
        return value.toString().trim().toLowerCase().replace(/\s+/g, "");
      };

      const isTransparentish = (value) => {
        const v = normaliseColor(value);
        return (
          v === "" ||
          v === "none" ||
          v === "transparent" ||
          v === "rgba(0,0,0,0)" ||
          v === "rgba(255,255,255,0)"
        );
      };

      const isBlackish = (value) => {
        const v = normaliseColor(value);

        if (
          v === "#000" ||
          v === "#000000" ||
          v === "black" ||
          v === "currentcolor" ||
          v === "rgb(0,0,0)"
        ) {
          return true;
        }

        // #rgb
        if (/^#[0-9a-f]{3}$/i.test(v)) {
          const r = parseInt(v[1] + v[1], 16);
          const g = parseInt(v[2] + v[2], 16);
          const b = parseInt(v[3] + v[3], 16);
          return r < 80 && g < 80 && b < 80;
        }

        // #rrggbb
        if (/^#[0-9a-f]{6}$/i.test(v)) {
          const r = parseInt(v.slice(1, 3), 16);
          const g = parseInt(v.slice(3, 5), 16);
          const b = parseInt(v.slice(5, 7), 16);
          return r < 80 && g < 80 && b < 80;
        }

        // rgb(...)
        const rgbMatch = v.match(/^rgb\((\d+),(\d+),(\d+)\)$/i);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1], 10);
          const g = parseInt(rgbMatch[2], 10);
          const b = parseInt(rgbMatch[3], 10);
          return r < 80 && g < 80 && b < 80;
        }

        return false;
      };

      const isWhiteish = (value) => {
        const v = normaliseColor(value);

        if (
          v === "#fff" ||
          v === "#ffffff" ||
          v === "white" ||
          v === "rgb(255,255,255)"
        ) {
          return true;
        }

        // #rgb
        if (/^#[0-9a-f]{3}$/i.test(v)) {
          const r = parseInt(v[1] + v[1], 16);
          const g = parseInt(v[2] + v[2], 16);
          const b = parseInt(v[3] + v[3], 16);
          return r > 220 && g > 220 && b > 220;
        }

        // #rrggbb
        if (/^#[0-9a-f]{6}$/i.test(v)) {
          const r = parseInt(v.slice(1, 3), 16);
          const g = parseInt(v.slice(3, 5), 16);
          const b = parseInt(v.slice(5, 7), 16);
          return r > 220 && g > 220 && b > 220;
        }

        // rgb(...)
        const rgbMatch = v.match(/^rgb\((\d+),(\d+),(\d+)\)$/i);
        if (rgbMatch) {
          const r = parseInt(rgbMatch[1], 10);
          const g = parseInt(rgbMatch[2], 10);
          const b = parseInt(rgbMatch[3], 10);
          return r > 220 && g > 220 && b > 220;
        }

        return false;
      };

      const parseRgb = (value) => {
        const v = normaliseColor(value);

        if (/^#[0-9a-f]{3}$/i.test(v)) {
          return [
            parseInt(v[1] + v[1], 16),
            parseInt(v[2] + v[2], 16),
            parseInt(v[3] + v[3], 16),
          ];
        }

        if (/^#[0-9a-f]{6}$/i.test(v)) {
          return [
            parseInt(v.slice(1, 3), 16),
            parseInt(v.slice(3, 5), 16),
            parseInt(v.slice(5, 7), 16),
          ];
        }

        const rgbMatch = v.match(/^rgb\((\d+),(\d+),(\d+)\)$/i);
        if (rgbMatch) {
          return [
            parseInt(rgbMatch[1], 10),
            parseInt(rgbMatch[2], 10),
            parseInt(rgbMatch[3], 10),
          ];
        }

        if (v === "black") return [0, 0, 0];
        if (v === "white") return [255, 255, 255];

        return null;
      };

      const isDarkOutlineColor = (value) => {
        const v = normaliseColor(value);

        if (!v || isTransparentish(v) || isWhiteish(v)) return false;
        if (v === "currentcolor") return true;

        const rgb = parseRgb(v);
        if (!rgb) return false;

        const [r, g, b] = rgb;
        return r < 90 && g < 90 && b < 90;
      };

      const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      const collectOriginalOutlineColors = (rawSvgText) => {
        const found = new Set();

        const attrRegex = /(fill|stroke)\s*=\s*["']([^"']+)["']/gi;
        let match;
        while ((match = attrRegex.exec(rawSvgText)) !== null) {
          const colorValue = match[2].trim();
          if (isDarkOutlineColor(colorValue)) {
            found.add(colorValue);
          }
        }

        const styleRegex = /(fill|stroke)\s*:\s*([^;"'}]+)/gi;
        while ((match = styleRegex.exec(rawSvgText)) !== null) {
          const colorValue = match[2].trim();
          if (isDarkOutlineColor(colorValue)) {
            found.add(colorValue);
          }
        }

        return Array.from(found);
      };

      const getStyleProp = (style, propName) => {
        if (!style) return null;
        const match = style.match(new RegExp(`${propName}\\s*:\\s*([^;]+)`, "i"));
        return match ? match[1].trim() : null;
      };

      const setStyleProp = (style, propName, propValue) => {
        if (!style || style.trim() === "") {
          return `${propName}:${propValue}`;
        }

        if (new RegExp(`${propName}\\s*:`, "i").test(style)) {
          return style.replace(
            new RegExp(`${propName}\\s*:\\s*[^;]+`, "gi"),
            `${propName}:${propValue}`
          );
        }

        return `${style.trim().replace(/;?$/, "")};${propName}:${propValue}`;
      };

      // 1) Remove fills if Fill checkbox is off
      if (!preserveWhiteFill) {
        elements.forEach((el) => {
          const fillAttr = el.getAttribute("fill");
          if (fillAttr && !isTransparentish(fillAttr)) {
            el.setAttribute("fill", "transparent");
          }

          const style = el.getAttribute("style");
          const styleFill = getStyleProp(style, "fill");
          if (styleFill && !isTransparentish(styleFill)) {
            el.setAttribute("style", setStyleProp(style, "fill", "transparent"));
          }
        });

        styleTags.forEach((tag) => {
          if (!tag.textContent) return;
          tag.textContent = tag.textContent.replace(
            /fill\s*:\s*([^;}]+)/gi,
            (match, value) => {
              if (isTransparentish(value) || normaliseColor(value) === "none") {
                return match;
              }
              return "fill:transparent";
            }
          );
        });
      }

      // 2) Apply Fill Color only to white/light fill areas
      if (preserveWhiteFill && fillColor) {
        const fillPaint = buildSvgFillPaint();
        elements.forEach((el) => {
          const fillAttr = el.getAttribute("fill");
          if (fillAttr && isWhiteish(fillAttr)) {
            el.setAttribute("fill", fillPaint);
          }

          const style = el.getAttribute("style");
          const styleFill = getStyleProp(style, "fill");
          if (styleFill && isWhiteish(styleFill)) {
            el.setAttribute("style", setStyleProp(style, "fill", fillPaint));
          }
        });

        styleTags.forEach((tag) => {
          if (!tag.textContent) return;
          tag.textContent = tag.textContent.replace(
            /fill\s*:\s*([^;}]+)/gi,
            (match, value) => {
              if (isWhiteish(value)) {
                return `fill:${fillPaint}`;
              }
              return match;
            }
          );
        });
      }

      
      // 3) Apply Outline Color to black strokes, black fills,
        // and shapes that rely on SVG's default black fill
        if (strokeColor) {
          const drawableTags = new Set([
            "path",
            "rect",
            "circle",
            "ellipse",
            "polygon",
            "polyline",
            "line",
          ]);

          elements.forEach((el) => {
            const tagName = el.tagName?.toLowerCase?.() || "";
            const isDrawable = drawableTags.has(tagName);

            const strokeAttr = el.getAttribute("stroke");
            const fillAttr = el.getAttribute("fill");
            const style = el.getAttribute("style");
            const styleStroke = getStyleProp(style, "stroke");
            const styleFill = getStyleProp(style, "fill");
            const className = el.getAttribute("class");

            let nextStyle = style || "";
            let changed = false;

            // Explicit stroke
            if (strokeAttr && isBlackish(strokeAttr)) {
              el.setAttribute("stroke", strokeColor);
              changed = true;
            }

            // Explicit fill
            if (fillAttr && isBlackish(fillAttr)) {
              el.setAttribute("fill", strokeColor);
              changed = true;
            }

            // Inline style stroke/fill
            if (styleStroke && isBlackish(styleStroke)) {
              nextStyle = setStyleProp(nextStyle, "stroke", strokeColor);
              changed = true;
            }

            if (styleFill && isBlackish(styleFill)) {
              nextStyle = setStyleProp(nextStyle, "fill", strokeColor);
              changed = true;
            }

            if (nextStyle && nextStyle !== style) {
              el.setAttribute("style", nextStyle);
            }

            // IMPORTANT:
            // If a drawable shape has no explicit fill/stroke/style/class,
            // it is usually using SVG's default fill, which is black.
            // That is exactly what your Bear outline is doing.
            const hasExplicitPaint =
              !!strokeAttr || !!fillAttr || !!styleStroke || !!styleFill;

            if (
              isDrawable &&
              !hasExplicitPaint &&
              !className
            ) {
              el.setAttribute("fill", strokeColor);
            }
          });

          styleTags.forEach((tag) => {
            if (!tag.textContent) return;

            tag.textContent = tag.textContent
              .replace(/stroke\s*:\s*([^;}]+)/gi, (match, value) => {
                return isBlackish(value) ? `stroke:${strokeColor}` : match;
              })
              .replace(/fill\s*:\s*([^;}]+)/gi, (match, value) => {
                return isBlackish(value) ? `fill:${strokeColor}` : match;
              });
          });
        }

        const serializer = new XMLSerializer();
        const finalSVG = serializer.serializeToString(svgEl);
        const encoded = encodeURIComponent('<?xml version="1.0"?>' + finalSVG);
        return `data:image/svg+xml;charset=utf-8,${encoded}`;

    } catch (err) {
      console.error("buildSvgDataUrl failed:", err);
      return null;
    }
  };
  

  // Handlers for Shape Opacity
  const handleShapeOpacityChange = (e) => {
    if (typeof recordPropertyChangeForUndo === 'function') {
      recordPropertyChangeForUndo('shape', 'opacity');
    }
    const newOpacity = parseFloat(e.target.value);
    setShapeProperties((prev) => ({ ...prev, opacity: newOpacity }));

    const updatedPages = [...pages];
    const currentShapes = updatedPages[activePage].shapes;

    const updatedShapes = currentShapes.map((shape) => {
      if (!selectedShapeIds.includes(shape.id.toString())) return shape;

      if (shape.type === 'maskedGroup' && Array.isArray(shape.children)) {
        // Apply opacity only to the content shape
        const [mask, content] = shape.children;
        const updatedContent = { ...content, opacity: newOpacity };
        return {
          ...shape,
          children: [mask, updatedContent],
        };
      }

      return { ...shape, opacity: newOpacity };
    });

    updatedPages[activePage].shapes = updatedShapes;
    setPages(updatedPages);
  };

  
  // Handlers for Shape Stroke Width
  const handleShapeStrokeWidthChange = (e) => {
    const value = parseFloat(e.target.value);
    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(e, 'strokeWidth');
    } else {
      setShapeProperties((prevProps) => ({
        ...prevProps,
        strokeWidth: value,
      }));
    }
  };

  // Handlers for Shape Stroke Color
  const handleShapeStrokeColorChange = (e) => {
    const value = e.target.value;
    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(e, 'strokeColor');
    } else {
      setShapeProperties((prevProps) => ({
        ...prevProps,
        strokeColor: value,
      }));
    }
  };

  // Handlers for Shape Fill Color
  const handleShapeFillColorChange = (e) => {
    const value = e.target.value;
    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(e, 'fillColor');
    } else {
      setShapeProperties((prevProps) => ({
        ...prevProps,
        fillColor: value,
      }));
    }
  };

  const handleFillStylePropertyChange = (property, value) => {
    const event = { target: { value } };
    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(event, property);
    } else {
      setShapeProperties((prevProps) => ({ ...prevProps, [property]: value }));
    }
  };

  const renderFillStyleControls = () => {
    if (!shapeProperties.fillEnabled) return null;

    const fillStyle = shapeProperties.fillStyle || 'solid';
    const isGradient = ['horizontalGradient', 'verticalGradient', 'diagonalGradient', 'radialGradient'].includes(fillStyle);
    const isPattern = !['solid', 'horizontalGradient', 'verticalGradient', 'diagonalGradient', 'radialGradient'].includes(fillStyle);

    return (
      <div className="fill-style-controls">
        <div className="property-group">
          <h3 className="property-heading">Fill Style:</h3>
          <select
            className="select-input"
            value={fillStyle}
            onFocus={pushToUndoStack}
            onChange={(e) => handleFillStylePropertyChange('fillStyle', e.target.value)}
          >
            <option value="solid">Solid</option>
            <option value="verticalGradient">Vertical Gradient</option>
            <option value="horizontalGradient">Horizontal Gradient</option>
            <option value="diagonalGradient">Diagonal Gradient</option>
            <option value="radialGradient">Radial Gradient</option>
            <option value="dots">Dots</option>
            <option value="hearts">Hearts</option>
            <option value="squares">Squares</option>
            <option value="stars">Stars</option>
            <option value="lines">Horizontal Lines</option>
            <option value="diagonalLines">Diagonal Lines</option>
            <option value="grid">Grid</option>
            <option value="checks">Checks</option>
            <option value="waves">Waves</option>
            <option value="zigzags">Zigzags</option>
            <option value="crosshatch">Crosshatch</option>
            <option value="confetti">Confetti</option>
          </select>
        </div>

        {(isGradient || isPattern) && (
          <div className="property-group">
            <h3 className="property-heading">{isGradient ? 'Second Color:' : 'Pattern Color:'}</h3>
            <input
              type="color"
              className="color-input"
              value={shapeProperties.fillSecondaryColor || '#F2D6A7'}
              onFocus={pushToUndoStack}
              onChange={(e) => handleFillStylePropertyChange('fillSecondaryColor', e.target.value)}
            />
            {renderColourSwatches(shapeProperties.fillSecondaryColor || '#F2D6A7', (colour) => {
              handleFillStylePropertyChange('fillSecondaryColor', colour);
            })}
          </div>
        )}

        {isPattern && (
          <div className="fill-pattern-settings">
            <label>
              Pattern Size: {shapeProperties.fillPatternSize || 10}
              <input
                type="range" min="3" max="40" step="1"
                className="opacity-slider"
                value={shapeProperties.fillPatternSize || 10}
                onChange={(e) => handleFillStylePropertyChange('fillPatternSize', e.target.value)}
              />
            </label>
            <label>
              Spacing: {shapeProperties.fillPatternSpacing ?? 8}
              <input
                type="range" min="0" max="40" step="1"
                className="opacity-slider"
                value={shapeProperties.fillPatternSpacing ?? 8}
                onChange={(e) => handleFillStylePropertyChange('fillPatternSpacing', e.target.value)}
              />
            </label>
            <label>
              Rotation: {shapeProperties.fillPatternRotation || 0}°
              <input
                type="range" min="0" max="180" step="5"
                className="opacity-slider"
                value={shapeProperties.fillPatternRotation || 0}
                onChange={(e) => handleFillStylePropertyChange('fillPatternRotation', e.target.value)}
              />
            </label>
          </div>
        )}
      </div>
    );
  };

  // New Handlers for Shape Stroke Width and Dashed Line
  const handleShapeStrokeWidth = (e) => {
    const value = parseFloat(e.target.value);
    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(e, 'strokeWidth');
    } else {
      setShapeProperties((prevProps) => ({
        ...prevProps,
        strokeWidth: value,
      }));
    }
  };

  const handleShapeDashEnabled = (e) => {
    const checked = e.target.checked;
    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(e, 'dashEnabled');
    } else {
      setShapeProperties((prevProps) => ({
        ...prevProps,
        dashEnabled: checked,
      }));
    }
  };

  const handleShapeDashPatternChange = (e) => {
    const value = e.target.value.split(',').map(Number);
    if (selectedShapeIds.length > 0) {
      updateShapePropertiesHandler(e, 'dash');
    } else {
      setShapeProperties((prevProps) => ({
        ...prevProps,
        dash: value,
      }));
    }
  };

const applySvgEditsToSelectedImage = (
  nextPreserveWhiteFill,
  nextFillColor,
  nextStrokeColor,
  nextFillStyle = imageFillStyle,
  nextFillSecondaryColor = imageFillSecondaryColor,
  nextFillPatternSize = imageFillPatternSize,
  nextFillPatternSpacing = imageFillPatternSpacing,
  nextFillPatternRotation = imageFillPatternRotation
) => {
  if (!selectedIsEditableSvgImage) return;

  if (typeof recordPropertyChangeForUndo === 'function') {
    recordPropertyChangeForUndo('svg-image', 'paint');
  }

  const original = selectedShape.meta.svgOriginal;
  const dataUrl = buildSvgDataUrl(
    original,
    nextPreserveWhiteFill,
    nextFillColor,
    nextStrokeColor,
    nextFillStyle,
    nextFillSecondaryColor,
    nextFillPatternSize,
    nextFillPatternSpacing,
    nextFillPatternRotation,
    Math.abs((selectedShape.width || 0) * (selectedShape.scaleX || 1)),
    Math.abs((selectedShape.height || 0) * (selectedShape.scaleY || 1))
  );

  if (!dataUrl) return;

  const updatedPages = [...pages];
  const shapes = updatedPages[activePage].shapes.map((shape) => {
    if (shape.id.toString() !== selectedShapeIds[0]) return shape;

    return {
      ...shape,
      src: dataUrl,
      meta: {
        ...(shape.meta || {}),
        svgOriginal: original,
        preserveWhiteFill: nextPreserveWhiteFill,
        fillColor: nextFillColor,
        strokeColor: nextStrokeColor,
        fillStyle: nextFillStyle,
        fillSecondaryColor: nextFillSecondaryColor,
        fillPatternSize: nextFillPatternSize,
        fillPatternSpacing: nextFillPatternSpacing,
        fillPatternRotation: nextFillPatternRotation,
        isSvg: true,
      },
    };
  });

  updatedPages[activePage].shapes = shapes;
  setPages(updatedPages);
};

  const applySvgFillStyleSetting = (property, value) => {
    const nextSettings = {
      fillStyle: imageFillStyle,
      fillSecondaryColor: imageFillSecondaryColor,
      fillPatternSize: imageFillPatternSize,
      fillPatternSpacing: imageFillPatternSpacing,
      fillPatternRotation: imageFillPatternRotation,
      [property]: value,
    };

    if (property === 'fillStyle') setImageFillStyle(value);
    if (property === 'fillSecondaryColor') setImageFillSecondaryColor(value);
    if (property === 'fillPatternSize') setImageFillPatternSize(Number(value));
    if (property === 'fillPatternSpacing') setImageFillPatternSpacing(Number(value));
    if (property === 'fillPatternRotation') setImageFillPatternRotation(Number(value));

    applySvgEditsToSelectedImage(
      imagePreserveWhiteFill,
      imageFillColor,
      imageStrokeColor,
      nextSettings.fillStyle,
      nextSettings.fillSecondaryColor,
      nextSettings.fillPatternSize,
      nextSettings.fillPatternSpacing,
      nextSettings.fillPatternRotation
    );
  };

  const renderSvgFillStyleControls = () => {
    if (!imagePreserveWhiteFill) return null;
    const isGradient = ['horizontalGradient', 'verticalGradient', 'diagonalGradient', 'radialGradient'].includes(imageFillStyle);
    const isPattern = !['solid', 'horizontalGradient', 'verticalGradient', 'diagonalGradient', 'radialGradient'].includes(imageFillStyle);

    return (
      <div className="fill-style-controls">
        <div className="property-group">
          <h3 className="property-heading">Fill Style:</h3>
          <select
            className="select-input"
            value={imageFillStyle}
            onFocus={pushToUndoStack}
            onChange={(e) => applySvgFillStyleSetting('fillStyle', e.target.value)}
          >
            <option value="solid">Solid</option>
            <option value="verticalGradient">Vertical Gradient</option>
            <option value="horizontalGradient">Horizontal Gradient</option>
            <option value="diagonalGradient">Diagonal Gradient</option>
            <option value="radialGradient">Radial Gradient</option>
            <option value="dots">Dots</option>
            <option value="hearts">Hearts</option>
            <option value="squares">Squares</option>
            <option value="stars">Stars</option>
            <option value="lines">Horizontal Lines</option>
            <option value="diagonalLines">Diagonal Lines</option>
            <option value="grid">Grid</option>
            <option value="checks">Checks</option>
            <option value="waves">Waves</option>
            <option value="zigzags">Zigzags</option>
            <option value="crosshatch">Crosshatch</option>
            <option value="confetti">Confetti</option>
          </select>
        </div>

        {(isGradient || isPattern) && (
          <div className="property-group">
            <h3 className="property-heading">{isGradient ? 'Second Color:' : 'Pattern Color:'}</h3>
            <input
              type="color"
              className="color-input"
              value={imageFillSecondaryColor}
              onFocus={pushToUndoStack}
              onChange={(e) => applySvgFillStyleSetting('fillSecondaryColor', e.target.value)}
            />
            {renderColourSwatches(imageFillSecondaryColor, (colour) => {
              applySvgFillStyleSetting('fillSecondaryColor', colour);
            })}
          </div>
        )}

        {isPattern && (
          <div className="fill-pattern-settings">
            <label>
              Pattern Size: {imageFillPatternSize}
              <input type="range" min="3" max="40" step="1" className="opacity-slider"
                value={imageFillPatternSize}
                onChange={(e) => applySvgFillStyleSetting('fillPatternSize', e.target.value)} />
            </label>
            <label>
              Spacing: {imageFillPatternSpacing}
              <input type="range" min="0" max="40" step="1" className="opacity-slider"
                value={imageFillPatternSpacing}
                onChange={(e) => applySvgFillStyleSetting('fillPatternSpacing', e.target.value)} />
            </label>
            <label>
              Rotation: {imageFillPatternRotation}°
              <input type="range" min="0" max="180" step="5" className="opacity-slider"
                value={imageFillPatternRotation}
                onChange={(e) => applySvgFillStyleSetting('fillPatternRotation', e.target.value)} />
            </label>
          </div>
        )}
      </div>
    );
  };

    const [copiedImagePromptStyle, setCopiedImagePromptStyle] = useState(null);

    const imagePromptStyles = [
    {
      name: 'Outline',
      thumbnail: OutlineThumbnail,
      prompt:
        'Create a simple black and white colouring page image on a clean white background. Use thick bold outlines, heavy uniform stroke weight, outline only, no shading, no grey, no colour, and minimal detail. Use rounded child-friendly shapes, simple vector style, clear open spaces for colouring, and an isolated centered subject with no background details. The subject is:',
    },
    {
      name: 'Crayon',
      thumbnail: CrayonThumbnail,
      prompt:
        'Create a cute children’s crayon-style illustration on a clean white background. The image should look hand-drawn with thick rough black outlines, waxy crayon texture, visible scribble marks, uneven colouring, and bright saturated colours. Use a playful kindergarten art style, simple bold shapes, slightly imperfect symmetry, soft white gaps in the colouring, and a cheerful handmade feel. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: 'Kids Cartoon',
      thumbnail: KidsCartoonThumbnail,
      prompt:
        'Create a bright kids cartoon-style illustration on a clean white background. Use bold clean outlines, cheerful colours, simple rounded shapes, expressive friendly features, and a fun preschool cartoon look. The image should be clear, cute, easy to recognise, and suitable for classroom materials. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: '3D Movie',
      thumbnail: ThreeDMovieThumbnail,
      prompt:
        'Create a bright 3D animated movie-style illustration on a clean white background. Use rounded toy-like shapes, soft studio lighting, smooth textures, expressive friendly design, and colourful child-friendly details. The image should look polished, cute, and suitable for young children, with a centered subject and no background details. The subject is:',
    },
    {
      name: 'Storybook',
      thumbnail: StorybookThumbnail,
      prompt:
        'Create a charming children’s storybook illustration on a clean white background. Use soft colours, gentle hand-drawn outlines, rounded friendly shapes, warm lighting, and a magical picture-book feel. The style should be simple enough for young children, with clear details, expressive character design, and a cosy classroom-friendly look. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: 'Doodle',
      thumbnail: DoodleThumbnail,
      prompt:
        'Create a playful children’s doodle-style illustration on a clean white background. Use loose hand-drawn black lines, simple sketchy shapes, small decorative doodle details, and a fun notebook drawing feel. Keep the image child-friendly, bold, simple, and easy to recognise. Use minimal colour accents if helpful, but keep the subject clear and centered with no background details. The subject is:',
    },
    {
      name: 'Fairytale',
      thumbnail: FairytaleThumbnail,
      prompt:
        'Create a cute fairytale-style children’s illustration on a clean white background. Use soft magical colours, gentle outlines, sparkling whimsical details, rounded friendly shapes, and a dreamy enchanted storybook feel. The image should look suitable for early years classroom resources, with a sweet, charming, non-scary style. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: 'Watercolour',
      thumbnail: WatercolourThumbnail,
      prompt:
        'Create a soft children’s watercolour-style illustration on a clean white background. Use gentle painted washes, light hand-drawn outlines, soft colour blending, simple rounded shapes, and a calm storybook feel. The image should be sweet, clear, child-friendly, and suitable for classroom materials. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: 'Paper Cutout',
      thumbnail: PaperCutoutThumbnail,
      prompt:
        'Create a playful paper cutout-style illustration on a clean white background. Use layered coloured paper shapes, soft shadows, simple rounded forms, visible paper texture, and a handmade classroom display feel. The image should be bold, child-friendly, easy to recognise, and suitable for printable learning resources. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: 'Felt Friends',
      thumbnail: FeltFriendsThumbnail,
      prompt:
        'Create a cute felt craft-style illustration on a clean white background. Use soft fabric texture, stitched edges, simple rounded shapes, gentle colours, and a handmade classroom craft feel. The image should look like a friendly felt character or felt cutout, child-friendly, clear, soft, and easy to recognise. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: 'Clay Folk',
      thumbnail: ClayFolkThumbnail,
      prompt:
        'Create a handmade clay folk-art style illustration on a clean white background. Use soft sculpted clay textures, simple rounded shapes, gentle handmade imperfections, warm earthy colours, and a charming handcrafted look. The image should feel like a small clay model made for children, with friendly details, soft lighting, and a clear centered subject. Keep the background plain white with no extra objects. The subject is:',
    },
    {
      name: 'Sticker',
      thumbnail: StickerThumbnail,
      prompt:
        'Create a cute sticker-style illustration on a clean white background. Use bold clean outlines, bright cheerful colours, simple rounded shapes, glossy sticker-like finish, and a thick white sticker border around the subject. The image should be fun, child-friendly, easy to recognise, and centered with no background details. The subject is:',
    },
    {
      name: 'Kawaii',
      thumbnail: KawaiiThumbnail,
      prompt:
        'Create a cute kawaii-style children’s illustration on a clean white background. Use simple rounded shapes, tiny friendly facial features, soft pastel colours, cheerful expression, clean outlines, and an adorable playful look. The image should be very clear, child-friendly, and suitable for early years classroom resources. Keep the subject centered with no background details. The subject is:',
    },
    {
      name: 'Photorealistic',
      thumbnail: PhotorealisticThumbnail,
      prompt:
        'Create a clean photorealistic image on a plain white background. Use realistic lighting, natural colours, clear details, and accurate textures. The subject should look like a high-quality studio photograph, isolated and centered, with no background objects, no text, and no distractions. The subject is:',
    },
  ];

  const copyImagePromptStyle = async (style) => {
    try {
      await navigator.clipboard.writeText(style.prompt);
      setCopiedImagePromptStyle(style.name);

      window.setTimeout(() => {
        setCopiedImagePromptStyle(null);
      }, 1200);
    } catch (err) {
      console.error('Clipboard copy failed:', err);
      alert('Failed to copy image prompt.');
    }
  };

  const renderImagePromptStyleGallery = () => {
    return (
      <div className="property-group image-prompt-style-card">
        <div className="image-prompt-style-header">
          <h3 className="property-heading image-prompt-style-title">
            AI Image Styles
          </h3>
          <p className="image-prompt-style-hint">
            Click a style to copy its prompt.
          </p>
        </div>

        <div className="image-prompt-style-grid">
          {imagePromptStyles.map((style) => {
            const isCopied = copiedImagePromptStyle === style.name;

            return (
              <button
                key={style.name}
                type="button"
                className={`image-prompt-style-button ${isCopied ? 'copied' : ''}`}
                onClick={() => copyImagePromptStyle(style)}
                title={`Copy ${style.name} prompt`}
                aria-label={`Copy ${style.name} image prompt`}
              >
                <img
                  src={style.thumbnail}
                  alt={`${style.name} style preview`}
                  className="image-prompt-style-thumbnail"
                  draggable="false"
                />

                <span className="image-prompt-style-name">
                  {style.name}
                </span>

                <span className="image-prompt-style-copy-label">
                  {isCopied ? 'Copied!' : 'Copy'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };


  const generateTracingLine = (type) => {
    const startX = 150;
    const startY = 150;
    const points = lineProperties.tracingPoints || 5;
    const length = lineProperties.tracingLength || 300;
    const height = lineProperties.tracingHeight || 50;
    const coords = [];
  
    if (type === 'zigzag') {
      for (let i = 0; i <= points; i++) {
        const x = startX + (length / points) * i;
        const y = startY + (i % 2 === 0 ? -height : height);
        coords.push(x, y);
      }
    }
  
    if (type === 'wavy') {
      const waveCount = points;
      const resolution = 50; // More = smoother
      for (let i = 0; i <= waveCount * resolution; i++) {
        const t = i / (waveCount * resolution); // Goes from 0 to 1
        const x = startX + t * length;
        const y = startY + Math.sin(t * waveCount * 2 * Math.PI) * height;
        coords.push(x, y);
      }
    }
  
      const newShape = {
        id: Date.now(),
        type: 'pencil',
        points: coords,
        x: 0,
        y: 0,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        strokeWidth: lineProperties.strokeWidth || 2,
        strokeColor: lineProperties.strokeColor || '#000000',
        opacity: 1,
        tension: type === 'wavy' ? 1 : 0,
        lineCap: 'round',
        lineJoin: 'round',
        dashEnabled: lineProperties.dashEnabled || false,
        dash: lineProperties.dashEnabled ? lineProperties.dash || [10, 5] : [],
        locked: false,
        meta: {
          tracingType: type,
          tracingPoints: points,
          tracingLength: length,
          tracingHeight: height,
        },
      };
    
  
    const updatedPages = [...pages];
    updatedPages[activePage].shapes.push(newShape);
    setPages(updatedPages);
    setSelectedShapeIds([newShape.id.toString()]);
    setSelectedTool('select');
  };

  const regenerateTracingLine = (shape, type, newPoints, newLength, newHeight) => {
    const startX = 150;
    const startY = 150;
    const height = newHeight || 50;    
    const coords = [];
  
    if (type === 'zigzag') {
      for (let i = 0; i <= newPoints; i++) {
        const x = startX + (newLength / newPoints) * i;
        const y = startY + (i % 2 === 0 ? -height : height);
        coords.push(x, y);
      }
    }
  
    if (type === 'wavy') {
      const waveCount = newPoints;
      const resolution = 50;
      for (let i = 0; i <= waveCount * resolution; i++) {
        const t = i / (waveCount * resolution);
        const x = startX + t * newLength;
        const y = startY + Math.sin(t * waveCount * 2 * Math.PI) * height;
        coords.push(x, y);
      }
    }
  
    const updatedPages = [...pages];
    const shapeIndex = updatedPages[activePage].shapes.findIndex((s) => s.id === shape.id);
  
    updatedPages[activePage].shapes[shapeIndex] = {
      ...shape,
      points: coords,
      strokeWidth: lineProperties.strokeWidth || 2,
      strokeColor: lineProperties.strokeColor || '#000000',
      dashEnabled: lineProperties.dashEnabled || false,
      dash: lineProperties.dashEnabled ? lineProperties.dash || [10, 5] : [],
      tension: type === 'wavy' ? 1 : 0,
      meta: {
        tracingType: type,
        tracingPoints: newPoints,
        tracingLength: newLength,
        tracingHeight: newHeight,
      },
    };
  
    setPages(updatedPages);
  };
  
  const includesPencilOrPen = pages[activePage]?.shapes
  ?.filter((shape) => selectedShapeIds.includes(shape.id.toString()))
  ?.some((shape) => shape?.type === 'pencil' || shape?.type === 'pen');

  const selectedShapes = selectedShapeIds.map((id) =>
    pages[activePage].shapes.find((s) => s.id.toString() === id)
  );

  const allSelectedAreMaskedGroups =
    selectedShapes.length > 0 &&
    selectedShapes.every((s) => s?.type === 'maskedGroup');


  const maskShape = selectedShapes.find(
    (s) =>
      (s?.type === 'pen' || s?.type === 'rect') &&
      (!s?.rotation || s?.rotation === 0) &&
      (s?.scaleX === 1 || s?.scaleX === undefined) &&
      (s?.scaleY === 1 || s?.scaleY === undefined)
  );


  const contentShape = selectedShapes.find(
    (s) => s?.type === 'image' || s?.type === 'text'
  );

  const showMaskingControls =
    (selectedShapeIds.length === 2 && maskShape && contentShape) ||
    (selectedShapeIds.length === 1 &&
      selectedShapes[0]?.type === 'maskedGroup');



    return (
      <div
        className={`tool-properties tool-properties-cursors ${isPropertiesCollapsed ? 'collapsed' : ''}`}
        style={panelCursorVars}
        data-tour="editor-properties"
      >
      <div className="collapse-button" onClick={togglePropertiesPanel}>
        <FontAwesomeIcon
          icon={isPropertiesCollapsed ? faChevronLeft : faChevronRight}
        />
      </div>
      {!isPropertiesCollapsed && (
        <div className="properties-content">
          {isMeasurementRulerSelected ? (
            <div className="measurement-ruler-tool">
              <div className="property-group">
                <h3 className="property-heading">Measurement Ruler</h3>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={isMeasurementRulerVisible}
                    onChange={(e) => {
                      const checked = e.target.checked;

                      setIsMeasurementRulerVisible(checked);

                      if (!checked) {
                        setIsMeasurementRulerSelected(false);
                      }
                    }}
                  />
                  Show 30 cm Ruler
                </label>
              </div>

              <div className="property-group">
                <h3 className="property-heading">Units:</h3>

                <select
                  value={measurementRulerSettings.unit}
                  onChange={(e) => {
                    setMeasurementRulerSettings((prev) => ({
                      ...prev,
                      unit: e.target.value,
                    }));
                  }}
                  className="select-input"
                >
                  <option value="cm">Centimetres and millimetres</option>
                  <option value="in">Inches and eighths</option>
                </select>
              </div>

              <div className="property-group">
                <h3 className="property-heading">Rotation:</h3>

                <input
                  type="number"
                  min="-360"
                  max="360"
                  step="1"
                  value={Math.round(measurementRulerSettings.rotation || 0)}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);

                    setMeasurementRulerSettings((prev) => ({
                      ...prev,
                      rotation: Number.isFinite(value) ? value : 0,
                    }));
                  }}
                  className="number-input"
                />
              </div>

              <div className="property-group">
                <button
                  type="button"
                  className="library-button"
                  onClick={() => {
                    setMeasurementRulerSettings((prev) => ({
                      ...prev,
                      x: pages[activePage].width / 2,
                      y: 120,
                      rotation: 0,
                    }));
                  }}
                >
                  Reset Ruler Position
                </button>
              </div>

              <div className="property-group">
                <p
                  style={{
                    margin: 0,
                    fontSize: '12px',
                    lineHeight: 1.4,
                    opacity: 0.7,
                  }}
                >
                  Drag the ruler using the Select tool. Use the round handle to rotate
                  it. This ruler will not appear in exports.
                </p>
              </div>
            </div>
          ) : selectedTool === 'bucket' ? (
            <div className="bucket-tool">
              <div className="property-group">
                <h3 className="property-heading">Common Colours:</h3>
                <div className="bucket-colour-grid">
                  {[
                    '#000000',
                    '#ffffff',
                    '#ff0000',
                    '#ff9900',
                    '#ffff00',
                    '#00cc00',
                    '#00bcd4',
                    '#0000ff',
                    '#8000ff',
                    '#ff00ff',
                    '#ffb6c1',
                    '#a52a2a',
                  ].map((colour) => (
                    <button
                      key={colour}
                      type="button"
                      className={`bucket-colour-swatch ${bucketSettings.color === colour ? 'active' : ''}`}
                      style={{ backgroundColor: colour }}
                      onClick={() =>
                        setBucketSettings((prev) => ({
                          ...prev,
                          color: colour,
                        }))
                      }
                      title={colour}
                      aria-label={`Select ${colour}`}
                    />
                  ))}
                </div>
              </div>

              <div className="property-group">
                <h3 className="property-heading">Bucket Colour:</h3>
                <input
                  type="color"
                  value={bucketSettings.color}
                  onChange={(e) =>
                    setBucketSettings((prev) => ({
                      ...prev,
                      color: e.target.value,
                    }))
                  }
                  className="color-input"
                />
              </div>

              <div className="property-group">
                <h3 className="property-heading">Affect:</h3>
                <select
                  value={bucketSettings.target}
                  onChange={(e) =>
                    setBucketSettings((prev) => ({
                      ...prev,
                      target: e.target.value,
                    }))
                  }
                  className="select-input"
                >
                  <option value="auto">Auto</option>
                  <option value="fill">Fill</option>
                  <option value="stroke">Stroke</option>
                  <option value="text">Text</option>
                </select>
              </div>

              <div className="property-group">
                <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>
                  Click any colour-changeable object on the canvas to apply this colour.
                </p>
              </div>
            </div>
          ) : selectedShapeIds.length > 0 ? (
            <>
              {allSelectedAreImages ? (
                <div className="image-tool">
                  {/* Opacity Slider for Images */}
                  <div className="property-group">
                    <h3 className="property-heading">Opacity:</h3>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={shapeProperties.opacity}
                      onChange={handleShapeOpacityChange}
                      className="opacity-slider"
                      onMouseUp={handleShapeOpacityChange} // Push to Undo stack on mouse up
                    />
                  </div>
                  {selectedIsEditableSvgImage && (
                    <div className="property-group tracing-line-controls">
                      <button
                        className="library-button"
                        onClick={() => onSwapImage(selectedShapeIds[0])}
                      >
                        <FontAwesomeIcon icon={faImages} style={{ marginRight: '6px' }} />
                        Swap Image
                      </button>
                    </div>
                  )}

                  {selectedIsEditableSvgImage && (
                    <>
                      {selectedSupportsSvgFillEditing && (
                        <>
                      <div className="property-group" style={{ marginTop: 10 }}>
                        <h3 className="property-heading">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={imagePreserveWhiteFill}
                              onChange={(e) => {
                                const next = e.target.checked;
                                setImagePreserveWhiteFill(next);
                                applySvgEditsToSelectedImage(next, imageFillColor, imageStrokeColor);
                              }}
                            />
                            Fill
                          </label>
                        </h3>
                      </div>

                      <div className="property-group">
                        <h3 className="property-heading">Fill Color:</h3>
                        <input
                          type="color"
                          value={imageFillColor}
                          onFocus={pushToUndoStack}
                          onChange={(e) => {
                            const next = e.target.value;
                            setImageFillColor(next);
                            applySvgEditsToSelectedImage(
                              imagePreserveWhiteFill,
                              next,
                              imageStrokeColor
                            );
                          }}
                          className="color-input"
                        />
                        {renderColourSwatches(imageFillColor || '#ffffff', (colour) => {
                          setImageFillColor(colour);
                          applySvgEditsToSelectedImage(
                            imagePreserveWhiteFill,
                            colour,
                            imageStrokeColor
                          );
                        })}
                      </div>

                      {renderSvgFillStyleControls()}

                        </>
                      )}

                      {selectedSupportsSvgOutlineEditing && (
                      <div className="property-group">
                        <h3 className="property-heading">Outline Color:</h3>
                        <input
                          type="color"
                          value={imageStrokeColor}
                          onFocus={pushToUndoStack}
                          onChange={(e) => {
                            const next = e.target.value;
                            setImageStrokeColor(next);
                            applySvgEditsToSelectedImage(
                              imagePreserveWhiteFill,
                              imageFillColor,
                              next
                            );
                          }}
                          className="color-input"
                        />
                        {renderColourSwatches(imageStrokeColor || '#000000', (colour) => {
                          setImageStrokeColor(colour);
                          applySvgEditsToSelectedImage(
                            imagePreserveWhiteFill,
                            imageFillColor,
                            colour
                          );
                        })}
                      </div>
                      )}
                    </>
                  )}

                </div>
              ) : allSelectedAreText ? (
                <div className="text-tool">
                  {/* Text Properties */}
                  <div className="property-group">
                  <textarea
                    value={textProperties.text}
                    onChange={(e) => updateTextPropertiesHandler(e, 'text')}
                    placeholder="Edit text here"
                    wrap="off"
                    spellCheck={false}
                    className="text-input export-filename-input multiline-text-input"
                  />
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">Alignment & Style:</h3>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        className={`shape-button ${textProperties.align === 'left' ? 'active' : ''}`}
                        onClick={() => updateTextPropertiesHandler({ target: { value: 'left' } }, 'align')}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-align-left" />
                      </button>
                      <button
                        className={`shape-button ${textProperties.align === 'center' ? 'active' : ''}`}
                        onClick={() => updateTextPropertiesHandler({ target: { value: 'center' } }, 'align')}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-align-center" />
                      </button>
                      <button
                        className={`shape-button ${textProperties.align === 'right' ? 'active' : ''}`}
                        onClick={() => updateTextPropertiesHandler({ target: { value: 'right' } }, 'align')}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-align-right" />
                      </button>
                      <button
                        className={`shape-button ${textProperties.bold ? 'active' : ''}`}
                        onClick={(e) => updateTextPropertiesHandler({ target: { checked: !textProperties.bold } }, 'bold')}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-bold" />
                      </button>
                      <button
                        className={`shape-button ${textProperties.italic ? 'active' : ''}`}
                        onClick={(e) => updateTextPropertiesHandler({ target: { checked: !textProperties.italic } }, 'italic')}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-italic" />
                      </button>
                    </div>
                  </div>


                  <div className="property-group">
                    <h3 className="property-heading">Font Family:</h3>

                    {renderFontFamilyPicker({
                      value: textProperties.fontFamily,
                      onChange: (e) => updateTextPropertiesHandler(e, 'fontFamily'),
                      pickerKey: 'selected-text-font-picker',
                    })}

                    {renderEducationalFontwareCredit(textProperties.fontFamily)}
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">Font Size:</h3>
                    <input
                      type="number"
                      min="0"
                      value={textProperties.fontSize}
                      onChange={(e) => updateTextPropertiesHandler(e, 'fontSize')}
                      className="number-input"
                    />
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">Text Color:</h3>
                    <input
                      type="color"
                      value={textProperties.fill}
                      onFocus={pushToUndoStack}
                      onChange={(e) => updateTextPropertiesHandler(e, 'fill')}
                      className="color-input"
                    />
                    {renderColourSwatches(textProperties.fill || '#000000', (colour) => {
                      updateTextPropertiesHandler({ target: { value: colour } }, 'fill');
                    })}
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">Opacity:</h3>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={textProperties.opacity}
                      onChange={(e) => updateTextPropertiesHandler(e, 'opacity')}
                      className="opacity-slider"
                      onMouseUp={(e) => updateTextPropertiesHandler(e, 'opacity')} // Push to Undo stack on mouse up
                    />
                  </div>
                  
                </div>
               ) : showPenShapeSettings ? (
                <div className="pen-shape">
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Width:</h3>
                    <input
                      type="number"
                      value={shapeProperties.strokeWidth}
                      onChange={(e) => updateShapePropertiesHandler(e, 'strokeWidth')}
                      className="number-input"
                    />
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={shapeProperties.strokeEnabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setShapeProperties((prev) => ({
                              ...prev,
                              strokeEnabled: checked,
                            }));
                            updateShapePropertiesHandler(e, 'strokeEnabled');
                          }}
                        />
                        Stroke Color:
                      </label>
                    </h3>
                    <input
                      type="color"
                      value={shapeProperties.strokeColor || '#000000'}
                      onFocus={pushToUndoStack}
                      onChange={(e) => updateShapePropertiesHandler(e, 'strokeColor')}
                      className="color-input"
                      disabled={!shapeProperties.strokeEnabled}
                    />
                    {shapeProperties.strokeEnabled &&
                      renderColourSwatches(shapeProperties.strokeColor || '#000000', (colour) => {
                        updateShapePropertiesHandler({ target: { value: colour } }, 'strokeColor');
                      })}
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={shapeProperties.fillEnabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setShapeProperties((prev) => ({
                              ...prev,
                              fillEnabled: checked,
                            }));
                            updateShapePropertiesHandler(e, 'fillEnabled');
                          }}
                        />
                        Fill Color:
                      </label>
                    </h3>
                    <input
                      type="color"
                      value={shapeProperties.fillColor || '#FFFFFF'}
                      onFocus={pushToUndoStack}
                      onChange={(e) => updateShapePropertiesHandler(e, 'fillColor')}
                      className="color-input"
                      disabled={!shapeProperties.fillEnabled}
                    />
                    {shapeProperties.fillEnabled &&
                      renderColourSwatches(shapeProperties.fillColor || '#ffffff', (colour) => {
                        updateShapePropertiesHandler({ target: { value: colour } }, 'fillColor');
                      })}
                  </div>

                  {renderFillStyleControls()}

                  <div className="property-group">
                    <h3 className="property-heading">Opacity:</h3>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={shapeProperties.opacity}
                      onChange={handleShapeOpacityChange}
                      className="opacity-slider"
                    />
                  </div>

                  {renderDashToggleCard({
                    checked: shapeProperties.dashEnabled || false,
                    onToggle: (e) => {
                      const checked = e.target.checked;

                      setShapeProperties((prev) => ({
                        ...prev,
                        dashEnabled: checked,
                        dash: checked ? (prev.dash?.length ? prev.dash : [10, 5]) : [],
                      }));

                      updateShapePropertiesHandler(e, 'dashEnabled');

                      updateShapePropertiesHandler(
                        {
                          target: {
                            value: checked
                              ? ((shapeProperties.dash?.length ? shapeProperties.dash : [10, 5]).join(','))
                              : '',
                          },
                        },
                        'dash'
                      );
                    },
                    dash: shapeProperties.dash,
                    onChangeDash: (nextDash) => {
                      setShapeProperties((prev) => ({
                        ...prev,
                        dash: nextDash,
                        dashEnabled: true,
                      }));

                      updateShapePropertiesHandler(
                        { target: { value: nextDash.join(',') } },
                        'dash'
                      );
                    },
                  })}

                  {selectedShape?.bezierEnabled && (
                    <div className="property-group">
                      <h3 className="property-heading">Bendy Line Editing:</h3>

                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedShape?.bezierSymmetricalHandles || false}
                          onChange={(e) => toggleSelectedBezierSymmetry(e.target.checked)}
                        />
                        Symmetrical Handles
                      </label>

                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
                        <button
                          className="library-button"
                          disabled={selectedBezierPointIndex === null}
                          onClick={() => setSelectedBezierPointMode('smooth')}
                          title="Make the selected point curved"
                        >
                          Smooth Point
                        </button>

                        <button
                          className="library-button"
                          disabled={selectedBezierPointIndex === null}
                          onClick={() => setSelectedBezierPointMode('corner')}
                          title="Make the selected point sharp"
                        >
                          Corner Point
                        </button>

                        <button
                          className="library-button"
                          disabled={selectedBezierPointIndex === null}
                          onClick={resetSelectedBezierPoint}
                          title="Reset selected point handles"
                        >
                          Reset Point
                        </button>

                        <button
                          className="library-button"
                          onClick={resetAllBezierPoints}
                          title="Reset all handles on this bendy line"
                        >
                          Reset All
                        </button>
                      </div>

                      <p style={{ margin: '8px 0 0', fontSize: '12px', opacity: 0.7 }}>
                        Select a yellow point on the canvas to edit that point.
                      </p>
                    </div>
                  )}
                </div>
                
              ) : allSelectedAreShapes && !allSelectedAreMaskedGroups ? (
                <div className="shape-tool">
                  {shapeProperties.type === 'grid' && (
                    <div className="custom-grid-row">
                      <div className="custom-grid-field">
                        <h3 className="property-heading">Rows</h3>
                        <input
                          type="number"
                          min="1"
                          value={shapeProperties.gridRows || 3}
                          onChange={(e) => updateShapePropertiesHandler(e, 'gridRows')}
                          className="number-input"
                        />
                      </div>

                      <div className="custom-grid-field">
                        <h3 className="property-heading">Columns</h3>
                        <input
                          type="number"
                          min="1"
                          value={shapeProperties.gridColumns || 3}
                          onChange={(e) => updateShapePropertiesHandler(e, 'gridColumns')}
                          className="number-input"
                        />
                      </div>
                    </div>
                  )}

                  {shapeProperties.type === 'circleSegments' && (
                    <div className="property-group">
                      <h3 className="property-heading">
                        Segments: {shapeProperties.segments || 4}
                      </h3>

                      <input
                        type="range"
                        min="1"
                        max="24"
                        step="1"
                        value={shapeProperties.segments || 4}
                        onChange={(e) => {
                          const value = Math.max(1, parseInt(e.target.value, 10) || 4);

                          setShapeProperties((prev) => ({
                            ...prev,
                            segments: value,
                          }));

                          updateShapePropertiesHandler(
                            { target: { value } },
                            'segments'
                          );
                        }}
                        className="opacity-slider"
                      />
                    </div>
                  )}

                  {shapeProperties.type === 'triangle' && (
                    <div className="property-group">
                      <h3 className="property-heading">
                        Peak Position: {shapeProperties.trianglePeakOffset || 0}
                      </h3>

                      <input
                        type="range"
                        min="-150"
                        max="150"
                        step="1"
                        value={shapeProperties.trianglePeakOffset || 0}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);

                          setShapeProperties((prev) => ({
                            ...prev,
                            trianglePeakOffset: value,
                          }));

                          updateShapePropertiesHandler(
                            { target: { value } },
                            'trianglePeakOffset'
                          );
                        }}
                        className="opacity-slider"
                      />
                    </div>
                  )}

                  {shapeProperties.type === 'star' && (
                    <div className="property-group">
                      <h3 className="property-heading">
                        Star Points: {shapeProperties.numPoints || 5}
                      </h3>

                      <input
                        type="range"
                        min="3"
                        max="20"
                        step="1"
                        value={shapeProperties.numPoints || 5}
                        onChange={(e) => {
                          const value = Math.max(3, Math.min(20, parseInt(e.target.value, 10) || 5));

                          setShapeProperties((prev) => ({
                            ...prev,
                            numPoints: value,
                          }));

                          updateShapePropertiesHandler(
                            { target: { value } },
                            'numPoints'
                          );
                        }}
                        className="opacity-slider"
                      />
                    </div>
                  )}

                  {shapeProperties.type === 'semiCircle' && (
                    <div className="property-group">
                      <h3 className="property-heading">
                        Arc Angle: {shapeProperties.arcAngle || 180}°
                      </h3>

                      <input
                        type="range"
                        min="0"
                        max="360"
                        step="1"
                        value={shapeProperties.arcAngle || 180}
                        onChange={(e) => {
                          let value = parseFloat(e.target.value);

                          if (isArcShiftPressed) {
                            value = Math.round(value / 22.5) * 22.5;
                          }

                          setShapeProperties((prev) => ({
                            ...prev,
                            arcAngle: value,
                          }));

                          updateShapePropertiesHandler(
                            { target: { value } },
                            'arcAngle'
                          );
                        }}
                        className="opacity-slider"
                      />

                      <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
                        Hold Shift for 22.5° steps
                      </div>
                    </div>
                  )}

                  {shapeProperties.type === 'maze' && (
                    <>
                      <div className="custom-grid-row">
                        <div className="custom-grid-field">
                          <h3 className="property-heading">Rows</h3>
                          <input
                            type="number"
                            min="1"
                            value={shapeProperties.mazeRows || 8}
                            onChange={(e) => updateShapePropertiesHandler(e, 'mazeRows')}
                            className="number-input"
                          />
                        </div>

                        <div className="custom-grid-field">
                          <h3 className="property-heading">Columns</h3>
                          <input
                            type="number"
                            min="1"
                            value={shapeProperties.mazeColumns || 8}
                            onChange={(e) => updateShapePropertiesHandler(e, 'mazeColumns')}
                            className="number-input"
                          />
                        </div>
                      </div>

                      <div className="custom-grid-row">
                        <div className="custom-grid-field">
                          <h3 className="property-heading">Exits</h3>
                          <input
                            type="number"
                            min="1"
                            max="4"
                            value={shapeProperties.mazeExits || 2}
                            onChange={(e) => updateShapePropertiesHandler(e, 'mazeExits')}
                            className="number-input"
                          />
                        </div>

                        <div className="custom-grid-field">
                          <h3 className="property-heading">Complexity</h3>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={shapeProperties.mazeComplexity || 3}
                            onChange={(e) => updateShapePropertiesHandler(e, 'mazeComplexity')}
                            className="number-input"
                          />
                        </div>
                      </div>

                      <div className="property-group tracing-line-controls" style={{ marginTop: '15px' }}>
                        <button
                          className="library-button"
                          onClick={regenerateSelectedMaze}
                        >
                          Regenerate Maze
                        </button>
                      </div>

                      <div className="property-group">
                        <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>
                          WARNING: Changing maze settings will regenerate the maze.
                          Higher complexity adds more misleading paths.
                        </p>
                      </div>
                    </>
                  )}

                  {shapeProperties.type === 'wordSearch' && (
                    <>
                      <div className="custom-grid-row">
                        <div className="custom-grid-field">
                          <h3 className="property-heading">Rows</h3>
                          <input
                            type="number"
                            min="1"
                            value={shapeProperties.wordSearchRows || 5}
                            onChange={(e) => updateShapePropertiesHandler(e, 'wordSearchRows')}
                            className="number-input"
                          />
                        </div>

                        <div className="custom-grid-field">
                          <h3 className="property-heading">Columns</h3>
                          <input
                            type="number"
                            min="1"
                            value={shapeProperties.wordSearchColumns || 5}
                            onChange={(e) => updateShapePropertiesHandler(e, 'wordSearchColumns')}
                            className="number-input"
                          />
                        </div>
                      </div>

                      <div className="property-group">
                        <h3 className="property-heading">Font Family:</h3>

                        {renderFontFamilyPicker({
                          value: shapeProperties.wordSearchFontFamily || 'Arial',
                          onChange: (e) =>
                            updateShapePropertiesHandler(e, 'wordSearchFontFamily'),
                          pickerKey: 'selected-text-cells-font-picker',
                        })}

                        {renderEducationalFontwareCredit(
                          shapeProperties.wordSearchFontFamily || 'Arial'
                        )}
                      </div>

                      <div className="property-group">
                        <h3 className="property-heading">Font Size:</h3>
                        <input
                          type="number"
                          min="6"
                          value={shapeProperties.wordSearchFontSize || 24}
                          onChange={(e) => updateShapePropertiesHandler(e, 'wordSearchFontSize')}
                          className="number-input"
                        />
                      </div>

                      <div className="checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={shapeProperties.wordSearchPressEnterToAdvance || false}
                            onChange={(e) => {
                              const checked = e.target.checked;

                              setShapeProperties((prevProps) => ({
                                ...prevProps,
                                wordSearchPressEnterToAdvance: checked,
                              }));

                              updateShapePropertiesHandler(e, 'wordSearchPressEnterToAdvance');
                            }}
                          />
                          Press enter to advance
                        </label>
                      </div>      

                      <div className="checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={shapeProperties.wordSearchPaintTextEnabled || false}
                            onChange={(e) => {
                              const checked = e.target.checked;

                              setShapeProperties((prevProps) => ({
                                ...prevProps,
                                wordSearchPaintTextEnabled: checked,
                              }));

                              updateShapePropertiesHandler(e, 'wordSearchPaintTextEnabled');
                            }}
                          />
                          Paint Cell Text
                        </label>
                      </div>

                      {shapeProperties.wordSearchPaintTextEnabled && (
                        <div className="property-group">
                          <h3 className="property-heading">Paint Value:</h3>
                          <input
                            type="text"
                            value={shapeProperties.wordSearchPaintTextValue || ''}
                            onChange={(e) => {
                              setShapeProperties((prevProps) => ({
                                ...prevProps,
                                wordSearchPaintTextValue: e.target.value,
                              }));

                              updateShapePropertiesHandler(e, 'wordSearchPaintTextValue');
                            }}
                            className="text-input"
                            placeholder="e.g. 1, 2, 7+3"
                          />
                        </div>
                      )}

                      {shapeProperties.wordSearchPaintTextEnabled && (
                        <div className="property-group">
                          <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>
                            Click or drag across cells to stamp this value into them.
                          </p>
                        </div>
                      )}          

                      
                    </>
                  )}

                  {/* Conditionally render Corner Radius only for Rounded Rectangles */}
                  {shapeProperties.type === 'roundedRect' && (
                    <div className="property-group">
                      <h3 className="property-heading">
                        Corner Radius: {shapeProperties.cornerRadius || 0}
                      </h3>

                      <input
                        type="range"
                        min="0"
                        max="100"
                        step="1"
                        value={shapeProperties.cornerRadius || 0}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || 0;

                          setShapeProperties((prev) => ({
                            ...prev,
                            cornerRadius: value,
                          }));

                          updateShapePropertiesHandler(
                            { target: { value } },
                            'cornerRadius'
                          );
                        }}
                        className="opacity-slider"
                      />
                    </div>
                  )}

                  {/* Stroke Width Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Width:</h3>
                    <input
                      type="number"
                      min="0"
                      value={shapeProperties.strokeWidth}
                      onChange={handleShapeStrokeWidthChange}
                      className="number-input"
                    />
                  </div>

                  {/* Stroke Color Property */}
                  <div className="property-group">
                    <h3 className="property-heading">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={allStrokeEnabled}
                          onChange={(e) => {
                            updateShapePropertiesHandler(e, 'strokeEnabled');
                          }}
                        />
                        Stroke Color:
                      </label>
                    </h3>
                    <input
                      type="color"
                      value={shapeProperties.strokeColor || '#000000'}
                      onFocus={pushToUndoStack}
                      onChange={handleShapeStrokeColorChange}
                      className="color-input"
                      disabled={!allStrokeEnabled}
                    />
                    {allStrokeEnabled &&
                      renderColourSwatches(shapeProperties.strokeColor || '#000000', (colour) => {
                        handleShapeStrokeColorChange({ target: { value: colour } });
                      })}
                  </div>

                  {/* Fill Color Property */}
                  <div className="property-group">
                    <h3 className="property-heading">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={allFillEnabled}
                          onChange={(e) => {
                            updateShapePropertiesHandler(e, 'fillEnabled');
                          }}
                        />
                        Fill Color:
                      </label>
                    </h3>
                    <input
                      type="color"
                      value={shapeProperties.fillColor || '#FFFFFF'}
                      onFocus={pushToUndoStack}
                      onChange={handleShapeFillColorChange}
                      className="color-input"
                      disabled={!allFillEnabled}
                    />
                    {allFillEnabled &&
                      renderColourSwatches(shapeProperties.fillColor || '#ffffff', (colour) => {
                        handleShapeFillColorChange({ target: { value: colour } });
                      })}
                  </div>


                  {renderFillStyleControls()}


                  {/* Opacity Slider */}
                  <div className="property-group">
                    <h3 className="property-heading">Opacity:</h3>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={shapeProperties.opacity}
                      onChange={handleShapeOpacityChange}
                      className="opacity-slider"
                      onMouseUp={handleShapeOpacityChange} // Push to Undo stack on mouse up
                    />
                  </div>

                  {selectedShape?.type === 'rect'
                    ? renderShapeStrokeStyleControls()
                    : renderBasicShapeDashControls()}
                    
                </div>

              ) : allSelectedAreMaskedGroups ? (
                <div className="masked-group-tool">
                  {/* Opacity Slider Only for Masked Groups */}
                  <div className="property-group">
                    <h3 className="property-heading">Opacity:</h3>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={shapeProperties.opacity}
                      onChange={handleShapeOpacityChange}
                      className="opacity-slider"
                      onMouseUp={handleShapeOpacityChange}
                    />
                  </div>
                </div>
        

              ) : allSelectedAreLines || isTracingLine || selectedTool === 'line' ? (
                <div className="line-tool">
                  {/* Stroke Width Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Width:</h3>
                    <input
                      type="number"
                      value={lineProperties.strokeWidth}
                      onChange={(e) => updateLinePropertiesHandler(e, 'strokeWidth')}
                      className="number-input"
                    />
                  </div>

                  {/* Stroke Color Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Color:</h3>
                    <input
                      type="color"
                      value={lineProperties.strokeColor}
                      onFocus={pushToUndoStack}
                      onChange={(e) => updateLinePropertiesHandler(e, 'strokeColor')}
                      className="color-input"
                    />
                    {allStrokeEnabled &&
                      renderColourSwatches(shapeProperties.strokeColor || '#000000', (colour) => {
                        handleShapeStrokeColorChange({ target: { value: colour } });
                      })}
                  </div>

                  {/* Line Opacity */}
                    <div className="property-group">
                      <h3 className="property-heading">Opacity:</h3>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={lineProperties.opacity ?? 1}
                        onChange={(e) => updateLinePropertiesHandler(e, 'opacity')}
                        className="opacity-slider"
                      />
                    </div>

                  {/* Dashed Line Checkbox */}
                  {renderDashToggleCard({
                    checked: lineProperties.dashEnabled,
                    onToggle: (e) => updateLinePropertiesHandler(e, 'dashEnabled'),
                    dash: lineProperties.dash,
                    onChangeDash: (nextDash) => {
                      updateLinePropertiesHandler(
                        { target: { value: nextDash.join(',') } },
                        'dash'
                      );
                    },
                  })}

                  {/* Zigzag / Wavy Line Generator */}
                {/* Only show tracing line generator if NOT editing an existing one */}
                {selectedTool === 'line' && selectedShapeIds.length === 0 && (
                    <div className="property-group">
                     <h3 className="property-heading">Insert Wave/Zigzag:</h3>
                     <button className="library-button" onClick={() => generateTracingLine('zigzag')}>
                      Insert Zigzag
                    </button>

                    <button className="library-button" onClick={() => generateTracingLine('wavy')}>
                      Insert Wave
                    </button>

                    <h3 className="property-heading">Points:</h3>

                      <input
                        type="number"
                        min="2"
                        max="100"
                        className="number-input"
                        value={lineProperties.tracingPoints || 5}
                        onChange={(e) =>
                          updateLinePropertiesHandler({ target: { value: parseInt(e.target.value) } }, 'tracingPoints')
                        }
                      />

                      <h3 className="property-heading">Length:</h3>
                      <input
                        type="number"
                        min="10"
                        max="1000"
                        className="number-input"
                        value={lineProperties.tracingLength || 300}
                        onChange={(e) =>
                          updateLinePropertiesHandler({ target: { value: parseInt(e.target.value) } }, 'tracingLength')
                        }
                      />

                      <h3 className="property-heading">Height:</h3>
                      <input
                        type="number"
                        min="10"
                        max="500"
                        className="number-input"
                        value={lineProperties.tracingHeight || 50}
                        onChange={(e) =>
                          updateLinePropertiesHandler(
                            { target: { value: parseInt(e.target.value) } },
                            'tracingHeight'
                          )
                        }
                      />

                    </div>
                  )}


                {selectedShape?.meta?.tracingType && (
                  <div className="property-group">
                    <h3 className="property-heading">Edit Wave/Zigzag:</h3>

                    <h3 className="property-heading">Points:</h3>

                    <input
                      type="number"
                      min="2"
                      max="100"
                      className="number-input"
                      value={selectedShape.meta.tracingPoints}
                      onChange={(e) =>
                        regenerateTracingLine(
                          selectedShape,
                          selectedShape.meta.tracingType,
                          parseInt(e.target.value),
                          selectedShape.meta.tracingLength
                        )
                      }
                    />

                    <h3 className="property-heading">Length:</h3>
                    <input
                      type="number"
                      min="10"
                      max="1000"
                      className="number-input"
                      value={selectedShape.meta.tracingLength}
                      onChange={(e) =>
                        regenerateTracingLine(
                          selectedShape,
                          selectedShape.meta.tracingType,
                          selectedShape.meta.tracingPoints,
                          parseInt(e.target.value)
                        )
                      }
                    />
                    
                    <h3 className="property-heading">Height:</h3>
                    <input
                      type="number"
                      min="10"
                      max="500"
                      className="number-input"
                      value={selectedShape.meta.tracingHeight || 50}
                      onChange={(e) =>
                        regenerateTracingLine(
                          selectedShape,
                          selectedShape.meta.tracingType,
                          selectedShape.meta.tracingPoints,
                          selectedShape.meta.tracingLength,
                          parseInt(e.target.value)
                        )
                      }
                    />

                  </div>
                )}
    

                </div>

              
              ) : allSelectedArePencilLines ? (
                <div className="pencil-tool">
                  {/* Stroke Width Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Width:</h3>
                    <input
                      type="number"
                      value={pencilProperties.strokeWidth}
                      onChange={(e) => updatePencilPropertiesHandler(e, 'strokeWidth')}
                      className="number-input"
                    />
                  </div>

                  {/* Smoothness (Tension) Slider */}
                  <div className="property-group">
                    <h3 className="property-heading">Smoothness:</h3>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.25"
                      value={pencilProperties.tension}
                      onChange={(e) => updatePencilPropertiesHandler(e, 'tension')}
                      className="opacity-slider"
                    />
                  </div>

                  {/* Stroke Color Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Color:</h3>
                    <input
                      type="color"
                      value={pencilProperties.strokeColor}
                      onFocus={pushToUndoStack}
                      onChange={(e) => updatePencilPropertiesHandler(e, 'strokeColor')}
                      className="color-input"
                    />
                    {renderColourSwatches(pencilProperties.strokeColor || '#000000', (colour) => {
                      updatePencilPropertiesHandler({ target: { value: colour } }, 'strokeColor');
                    })}
                  </div>

                  {/* Pencil Opacity */}
                  <div className="property-group">
                    <h3 className="property-heading">Opacity:</h3>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={pencilProperties.opacity}
                      onChange={(e) => updatePencilPropertiesHandler(e, 'opacity')}
                      className="opacity-slider"
                    />
                  </div>

                 
                  
                  {/* Dashed Line Checkbox */}
                  {renderDashToggleCard({
                    checked: pencilProperties.dashEnabled,
                    onToggle: (e) => {
                      const checked = e.target.checked;
                      const nextDash = checked
                        ? (pencilProperties.dash?.length ? pencilProperties.dash : [10, 5])
                        : [];

                      updatePencilPropertiesHandler(
                        { target: { value: nextDash.join(',') } },
                        'dash'
                      );

                      updatePencilPropertiesHandler(
                        { target: { checked } },
                        'dashEnabled'
                      );
                    },
                    dash: pencilProperties.dash,
                    onChangeDash: (nextDash) => {
                      updatePencilPropertiesHandler(
                        { target: { value: nextDash.join(',') } },
                        'dash'
                      );
                    },
                  })}
                </div>
              ) : (
                <div className="mixed-shapes-tool">
                  <p>Selected shapes have mixed types. Editing is limited to common properties.</p>
                  {/* Common Properties */}
                  {/* Stroke Width Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Width:</h3>
                    <input
                      type="number"
                      value={shapeProperties.strokeWidth}
                      onChange={(e) => updateShapePropertiesHandler(e, 'strokeWidth')}
                      className="number-input"
                    />
                  </div>

                  {/* Stroke Color Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Stroke Color:</h3>
                    <input
                      type="color"
                      value={shapeProperties.strokeColor}
                      onFocus={pushToUndoStack}
                      onChange={handleShapeStrokeColorChange}
                      className="color-input"
                    />
                    {allStrokeEnabled &&
                      renderColourSwatches(shapeProperties.strokeColor || '#000000', (colour) => {
                        handleShapeStrokeColorChange({ target: { value: colour } });
                      })}
                  </div>

                  {/* Fill Color Property */}
                  <div className="property-group">
                    <h3 className="property-heading">Fill Color:</h3>
                    <input
                      type="color"
                      value={shapeProperties.fillColor}
                      onFocus={pushToUndoStack}
                      onChange={handleShapeFillColorChange}
                      className="color-input"
                    />
                    {allFillEnabled &&
                      renderColourSwatches(shapeProperties.fillColor || '#ffffff', (colour) => {
                        handleShapeFillColorChange({ target: { value: colour } });
                      })}
                  </div>

                  {renderFillStyleControls()}

                  {/* Opacity Slider */}
                  <div className="property-group">
                    <h3 className="property-heading">Opacity:</h3>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={shapeProperties.opacity}
                      onChange={handleShapeOpacityChange}
                      className="opacity-slider"
                      onMouseUp={handleShapeOpacityChange} // Push to Undo stack on mouse up
                    />
                  </div>                 
                  

                  {selectedShape?.type === 'rect'
                    ? renderShapeStrokeStyleControls()
                    : renderBasicShapeDashControls()}
                    
                </div>
                
              )}
              {/* 🔄 Align & Distribute Buttons (Select Tool Only) */}
              {selectedTool === 'select' && (
              <>
                {/* 🔄 Align & Distribute */}
                {selectedShapeIds.length >= 2 && (
                  <div className="property-group">
                    <h3 className="property-heading">Align & Distribute:</h3>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        className="shape-button"
                        onClick={alignSelectionHorizontally}
                        title="Align Horizontally"
                      >
                        <FontAwesomeIcon icon="fa-solid fa-arrows-alt-v" />
                      </button>
                      <button
                        className="shape-button"
                        onClick={alignSelectionVertically}
                        title="Align Vertically"
                      >
                        <FontAwesomeIcon icon="fa-solid fa-arrows-alt-h" />
                      </button>
                      {selectedShapeIds.length >= 3 && (
                        <>
                          <button
                            className="shape-button"
                            onClick={distributeSelectionHorizontally}
                            title="Distribute Horizontally"
                          >
                            <FontAwesomeIcon
                              icon="fa-solid fa-align-justify"
                              style={{ transform: 'rotate(90deg)' }}
                            />
                          </button>
                          <button
                            className="shape-button"
                            onClick={distributeSelectionVertically}
                            title="Distribute Vertically"
                          >
                            <FontAwesomeIcon icon="fa-solid fa-align-justify" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Group Buttons — always shown if 2+ shapes selected */}
                  {selectedTool === 'select' && (selectedShapeIds.length >= 2 || showMaskingControls) && (
                    <div className="property-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '40px', flexWrap: 'wrap' }}>
                        {/* Group Buttons */}
                        {selectedShapeIds.length >= 2 && (
                          <div>
                            <h3 className="property-heading">Group:</h3>
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <button
                                className={`shape-button ${isGroupSelection ? 'edit-mode-active' : ''}`}
                                onClick={groupSelectedShapes}
                                title="Group Selected Shapes"
                              >
                                <FontAwesomeIcon icon="fa-solid fa-object-group" />
                              </button>
                              <button
                                className="shape-button"
                                onClick={ungroupSelectedShapes}
                                title="Ungroup Selected Shapes"
                              >
                                <FontAwesomeIcon icon="fa-solid fa-object-ungroup" />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* ✅ Masking Buttons */}
                          {showMaskingControls && (
                            <div>
                              <h3 className="property-heading">Masking:</h3>
                              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                {/* Apply Mask */}
                                <button
                                  className={`shape-button ${selectedShapes[0]?.type === 'maskedGroup' ? 'edit-mode-active' : ''}`}
                                  onClick={onApplyPenMask}
                                  title="Create Mask"
                                >
                                  <FontAwesomeIcon icon="fa-solid fa-scissors" />
                                </button>

                                {/* Release Mask */}
                                <button
                                  className="shape-button"
                                  onClick={onReleasePenMask}
                                  title="Release Mask"
                                >
                                  <FontAwesomeIcon icon="fa-solid fa-eraser" />
                                </button>

                                {/* 🔁 Reverse Mask */}
                                {selectedShapes[0]?.type === 'maskedGroup' && (
                                  <button
                                    className="shape-button"
                                    onClick={() => {
                                      pushToUndoStack();
                                      const updatedPages = [...pages];
                                      const shapeIndex = updatedPages[activePage].shapes.findIndex(
                                        (s) => s.id.toString() === selectedShapes[0].id.toString()
                                      );
                                      if (shapeIndex !== -1) {
                                        const shape = updatedPages[activePage].shapes[shapeIndex];
                                        shape.reverseMask = !shape.reverseMask;
                                        setPages(updatedPages);
                                      }
                                    }}
                                    title="Reverse Mask"
                                  >
                                    <FontAwesomeIcon icon="fa-solid fa-sync-alt" />
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {/* Flip Buttons — only if no pencil or pen included */}
                  {selectedTool === 'select' && selectedShapeIds.length >= 1 && !includesPencilOrPen && (
                    <div className="property-group">
                      <h3 className="property-heading">Flip:</h3>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        <button
                          className="shape-button"
                          onClick={flipSelectedHorizontally}
                          title="Flip Horizontally"
                        >
                          <FontAwesomeIcon icon="fa-solid fa-arrows-alt-h" />
                        </button>
                        <button
                          className="shape-button"
                          onClick={flipSelectedVertically}
                          title="Flip Vertically"
                        >
                          <FontAwesomeIcon icon="fa-solid fa-arrows-alt-v" />
                        </button>
                      </div>
                    </div>
                  )}

              </>
            )}


              
            </>

          ) : showPenSettings ? (
            <div className="pen-tool">
              <div className="property-group">
                <h3 className="property-heading">Stroke Width:</h3>
                <input
                  type="number"
                  value={penToolSettings.strokeWidth}
                  onChange={(e) =>
                    setPenToolSettings((prev) => ({
                      ...prev,
                      strokeWidth: parseInt(e.target.value),
                    }))
                  }
                  className="number-input"
                />
              </div>

              <div className="property-group">
                <h3 className="property-heading">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={penToolSettings.strokeEnabled}
                      onChange={(e) =>
                        setPenToolSettings((prev) => ({
                          ...prev,
                          strokeEnabled: e.target.checked,
                        }))
                      }
                    />
                    Stroke Color:
                  </label>
                </h3>
                <input
                  type="color"
                  value={penToolSettings.strokeColor || '#000000'}
                  onChange={(e) =>
                    setPenToolSettings((prev) => ({
                      ...prev,
                      strokeColor: e.target.value,
                    }))
                  }
                  className="color-input"
                  disabled={!penToolSettings.strokeEnabled}
                />
                {penToolSettings.strokeEnabled &&
                  renderColourSwatches(penToolSettings.strokeColor || '#000000', (colour) => {
                    setPenToolSettings((prev) => ({
                      ...prev,
                      strokeColor: colour,
                    }));
                  })}
              </div>

              <div className="property-group">
                <h3 className="property-heading">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={penToolSettings.fillEnabled}
                      onChange={(e) =>
                        setPenToolSettings((prev) => ({
                          ...prev,
                          fillEnabled: e.target.checked,
                        }))
                      }
                    />
                    Fill Color:
                  </label>
                </h3>
                <input
                  type="color"
                  value={penToolSettings.fillColor || '#FFFFFF'}
                  onChange={(e) =>
                    setPenToolSettings((prev) => ({
                      ...prev,
                      fillColor: e.target.value,
                    }))
                  }
                  className="color-input"
                  disabled={!penToolSettings.fillEnabled}
                />
                {penToolSettings.fillEnabled &&
                  renderColourSwatches(penToolSettings.fillColor || '#ffffff', (colour) => {
                    setPenToolSettings((prev) => ({
                      ...prev,
                      fillColor: colour,
                    }));
                  })}
              </div>

              <div className="property-group">
                <h3 className="property-heading">Opacity:</h3>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={penToolSettings.opacity}
                  onChange={(e) =>
                    setPenToolSettings((prev) => ({
                      ...prev,
                      opacity: parseFloat(e.target.value),
                    }))
                  }
                  className="opacity-slider"
                />
              </div>

              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={penToolSettings.bezierEnabled || false}
                    onChange={(e) =>
                      setPenToolSettings((prev) => ({
                        ...prev,
                        bezierEnabled: e.target.checked,
                      }))
                    }
                  />
                  Bendy Lines
                </label>
              </div>

              {renderDashToggleCard({
                checked: penToolSettings.dashEnabled,
                onToggle: (e) => {
                  const checked = e.target.checked;

                  setPenToolSettings((prev) => ({
                    ...prev,
                    dashEnabled: checked,
                    dash: checked ? (prev.dash?.length ? prev.dash : [10, 5]) : [],
                  }));
                },
                dash: penToolSettings.dash,
                onChangeDash: (nextDash) => {
                  setPenToolSettings((prev) => ({
                    ...prev,
                    dash: nextDash,
                    dashEnabled: true,
                  }));
                },
              })}
            </div>          
      
            
          ) : selectedTool === 'text' ? (
            <div className="text-tool">
              {/* Text Properties */}            
              <div className="property-group">
              <textarea
                value={textProperties.text}
                onChange={(e) => updateTextPropertiesHandler(e, 'text')}
                placeholder="Edit text here"
                wrap="off"
                spellCheck={false}
                className="text-input export-filename-input multiline-text-input"
              />
              </div>            

              <div className="property-group">
                <h3 className="property-heading">Alignment & Style:</h3>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    className={`shape-button ${textProperties.align === 'left' ? 'active' : ''}`}
                    onClick={() => updateTextPropertiesHandler({ target: { value: 'left' } }, 'align')}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-align-left" />
                  </button>
                  <button
                    className={`shape-button ${textProperties.align === 'center' ? 'active' : ''}`}
                    onClick={() => updateTextPropertiesHandler({ target: { value: 'center' } }, 'align')}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-align-center" />
                  </button>
                  <button
                    className={`shape-button ${textProperties.align === 'right' ? 'active' : ''}`}
                    onClick={() => updateTextPropertiesHandler({ target: { value: 'right' } }, 'align')}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-align-right" />
                  </button>
                  <button
                    className={`shape-button ${textProperties.bold ? 'active' : ''}`}
                    onClick={() =>
                      updateTextPropertiesHandler({ target: { checked: !textProperties.bold } }, 'bold')
                    }
                  >
                    <FontAwesomeIcon icon="fa-solid fa-bold" />
                  </button>
                  <button
                    className={`shape-button ${textProperties.italic ? 'active' : ''}`}
                    onClick={() =>
                      updateTextPropertiesHandler({ target: { checked: !textProperties.italic } }, 'italic')
                    }
                  >
                    <FontAwesomeIcon icon="fa-solid fa-italic" />
                  </button>
                </div>
              </div>



              <div className="property-group">
                <h3 className="property-heading">Font Family:</h3>

                {renderFontFamilyPicker({
                  value: textProperties.fontFamily,
                  onChange: (e) => updateTextPropertiesHandler(e, 'fontFamily'),
                  pickerKey: 'new-text-font-picker',
                })}

                {renderEducationalFontwareCredit(textProperties.fontFamily)}
              </div>

              <div className="property-group">
                <h3 className="property-heading">Font Size:</h3>
                <input
                  type="number"
                  value={textProperties.fontSize}
                  onChange={(e) => updateTextPropertiesHandler(e, 'fontSize')}
                  className="number-input"
                />
              </div>

              <div className="property-group">
                <h3 className="property-heading">Text Color:</h3>
                <input
                  type="color"
                  value={textProperties.fill}
                  onChange={(e) => updateTextPropertiesHandler(e, 'fill')}
                  className="color-input"
                />
                {renderColourSwatches(textProperties.fill || '#000000', (colour) => {
                  updateTextPropertiesHandler({ target: { value: colour } }, 'fill');
                })}
              </div>

              <div className="property-group">
                <h3 className="property-heading">Opacity:</h3>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={textProperties.opacity}
                  onChange={(e) => updateTextPropertiesHandler(e, 'opacity')}
                  className="opacity-slider"
                  onMouseUp={(e) => updateTextPropertiesHandler(e, 'opacity')} // Push to Undo stack on mouse up
                />
              </div>

              
            </div>
          ) : selectedTool === 'addImage' ? (
            <div className="image-tool-properties">
              <button className="upload-button" onClick={triggerImageUpload}>
                <FontAwesomeIcon icon={faFileUpload} /> Upload
              </button>

              <button className="library-button" onClick={() => setShowImageLibraryModal(true)}>
                <FontAwesomeIcon icon={faImages} /> Image Library
              </button>

              {renderImagePromptStyleGallery()}

              {/* Only show Insert Logo if user is admin */}
              {currentUser?.isAdmin && (
                <>
                  <h3>Insert Logo</h3>
                  <button onClick={() => onSetLogo()}>
                    Set Rectangle Logo
                  </button>
                  <button onClick={() => onSetSquareLogo()}>
                    Set Square Logo
                  </button>
                </>
              )}
            </div>
          ) : selectedTool === 'save' ? (
            <div className="eg-save-panel">
              <div className="eg-save-card">
                <label className="eg-save-label">File Name</label>

                <div
                  className="eg-save-input-frame"
                  onMouseDown={() => {
                    if (saveFilenameInputRef.current) {
                      saveFilenameInputRef.current.focus();
                    }
                  }}
                >
                  <input
                    ref={saveFilenameInputRef}
                    type="text"
                    value={exportFilename}
                    onChange={(e) => setExportFilename(e.target.value)}
                    className="eg-save-name-input"
                    placeholder="Enter file name"
                  />
                </div>
              </div>

              <div className="eg-save-actions">
                {currentUser?.isAdmin && (
                  <button className="save-button eg-upload-template-button" onClick={onUploadTemplateClick}>
                    Upload Template
                  </button>
                )}

                <div className="eg-file-action-grid">
                  {currentUser && !isBasic && (
                    <button
                      className="eg-file-action"
                      onClick={handleTrackedSaveStateAsJSON}
                      data-tooltip="Save all editable pages as a .groove project file."
                    >
                      <FontAwesomeIcon icon={faFileDownload} />
                      <span>Download<br />.Groove</span>
                    </button>
                  )}

                  {currentUser && !isBasic && (
                    <button
                      className="eg-file-action"
                      onClick={() => triggerFileInput('replace')}
                      data-tooltip="Open a .groove file and replace your current pages."
                    >
                      <FontAwesomeIcon icon={faFileUpload} />
                      <span>Open<br />.Groove</span>
                    </button>
                  )}

                  {currentUser && !isBasic && (
                    <button
                      className="eg-file-action"
                      onClick={() => triggerFileInput('append')}
                      data-tooltip="Add every page from a .groove file after your current pages."
                    >
                      <FontAwesomeIcon icon={faFileUpload} />
                      <span>Append<br />.Groove</span>
                    </button>
                  )}

                  <button
                    className="eg-file-action"
                    onClick={triggerPDFInput}
                    data-tooltip="Import each page of a PDF as a new worksheet page."
                  >
                    <FontAwesomeIcon icon={faFileUpload} />
                    <span>Load<br />PDF</span>
                  </button>

                  <button
                    className="eg-file-action"
                    onClick={handleTrackedExportToPDF}
                    data-tooltip="Download your pages as a standard PDF."
                  >
                    <FontAwesomeIcon icon={faFileExport} />
                    <span>Export<br />PDF</span>
                  </button>

                  <button
                    className="eg-file-action"
                    onClick={handleOpenAdvancedPDFExport}
                    data-tooltip="Create tiled posters, giant prints and other advanced PDF layouts."
                  >
                    <FontAwesomeIcon icon={faFileExport} />
                    <span>Advanced<br />PDF</span>
                  </button>
                </div>
              </div>
            </div>
          ) : selectedTool === 'shape' ? (
            <div className="shape-tool-section">
              <div className="shape-selection">
                {Object.keys(shapeSVGs).map((shape) => {
                  const shapeTooltips = {
                    grid: 'Grid',
                    circleSegments: 'Circle Segments',
                    maze: 'Maze',
                    wordSearch: 'Text Cells',
                  };

                  return (
                    <button
                      key={shape}
                      onClick={() => {
                        setSelectedShapeType(shape);

                        if (shape !== 'rect') {
                          setShapeProperties((prev) => ({
                            ...prev,
                            strokeStyle: 'solid',
                            dashEnabled: false,
                            dash: [10, 5],
                            strokeWaveSize: 8,
                            strokeWaveFrequency: 6,
                          }));
                        }
                      }}
                      className={selectedShapeType === shape ? 'active shape-button' : 'shape-button'}
                      aria-label={shape.charAt(0).toUpperCase() + shape.slice(1)}
                      title={shapeTooltips[shape] || ''}
                    >
                      {shapeSVGs[shape]}
                    </button>
                  );
                })}
              </div>

              {selectedShapeType === 'grid' && (
                <div className="custom-grid-row">
                  <div className="custom-grid-field">
                    <h3 className="property-heading">Rows</h3>
                    <input
                      type="number"
                      min="1"
                      value={shapeProperties.gridRows || 3}
                      onChange={(e) =>
                        setShapeProperties((prevProps) => ({
                          ...prevProps,
                          gridRows: Math.max(1, parseInt(e.target.value, 10) || 1),
                        }))
                      }
                      className="number-input"
                    />
                  </div>

                  <div className="custom-grid-field">
                    <h3 className="property-heading">Columns</h3>
                    <input
                      type="number"
                      min="1"
                      value={shapeProperties.gridColumns || 3}
                      onChange={(e) =>
                        setShapeProperties((prevProps) => ({
                          ...prevProps,
                          gridColumns: Math.max(1, parseInt(e.target.value, 10) || 1),
                        }))
                      }
                      className="number-input"
                    />
                  </div>
                </div>
              )}

              {selectedShapeType === 'wordSearch' && (
                <>
                  <div className="custom-grid-row">
                    <div className="custom-grid-field">
                      <h3 className="property-heading">Rows</h3>

                      <input
                        type="number"
                        min="1"
                        value={shapeProperties.wordSearchRows || 5}
                        onChange={(e) =>
                          setShapeProperties((prevProps) => ({
                            ...prevProps,
                            wordSearchRows: Math.max(
                              1,
                              parseInt(e.target.value, 10) || 1
                            ),
                          }))
                        }
                        className="number-input"
                      />
                    </div>

                    <div className="custom-grid-field">
                      <h3 className="property-heading">Columns</h3>

                      <input
                        type="number"
                        min="1"
                        value={shapeProperties.wordSearchColumns || 5}
                        onChange={(e) =>
                          setShapeProperties((prevProps) => ({
                            ...prevProps,
                            wordSearchColumns: Math.max(
                              1,
                              parseInt(e.target.value, 10) || 1
                            ),
                          }))
                        }
                        className="number-input"
                      />
                    </div>
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">Font Family:</h3>

                    {renderFontFamilyPicker({
                      value: shapeProperties.wordSearchFontFamily || 'Arial',
                      onChange: (e) =>
                        setShapeProperties((prevProps) => ({
                          ...prevProps,
                          wordSearchFontFamily: e.target.value,
                        })),
                      pickerKey: 'new-text-cells-font-picker',
                    })}

                    {renderEducationalFontwareCredit(
                      shapeProperties.wordSearchFontFamily || 'Arial'
                    )}
                  </div>

                  <div className="property-group">
                    <h3 className="property-heading">Font Size:</h3>

                    <input
                      type="number"
                      min="6"
                      value={shapeProperties.wordSearchFontSize || 24}
                      onChange={(e) =>
                        setShapeProperties((prevProps) => ({
                          ...prevProps,
                          wordSearchFontSize: Math.max(
                            6,
                            parseInt(e.target.value, 10) || 24
                          ),
                        }))
                      }
                      className="number-input"
                    />
                  </div>
                </>
              )}

              {selectedShapeType === 'star' && selectedShapeIds.length === 0 && (
                <div className="property-group">
                  <h3 className="property-heading">
                    Star Points: {shapeProperties.numPoints || 5}
                  </h3>

                  <input
                    type="range"
                    min="3"
                    max="20"
                    step="1"
                    value={shapeProperties.numPoints || 5}
                    onChange={(e) => {
                      const value = Math.max(3, Math.min(20, parseInt(e.target.value, 10) || 5));

                      setShapeProperties((prev) => ({
                        ...prev,
                        numPoints: value,
                      }));
                    }}
                    className="opacity-slider"
                  />
                </div>
              )}

              {selectedShapeType === 'circleSegments' && (
                <div className="property-group">
                  <h3 className="property-heading">Segments</h3>
                  <input
                    type="number"
                    min="1"
                    value={shapeProperties.segments || 4}
                    onChange={(e) =>
                      setShapeProperties((prevProps) => ({
                        ...prevProps,
                        segments: Math.max(1, parseInt(e.target.value, 10) || 1),
                      }))
                    }
                    className="number-input"
                  />
                </div>
              )}

              {selectedShapeType === 'maze' && (
                <>
                  <div className="custom-grid-row">
                    <div className="custom-grid-field">
                      <h3 className="property-heading">Rows</h3>
                      <input
                        type="number"
                        min="1"
                        value={shapeProperties.mazeRows || 8}
                        onChange={(e) =>
                          setShapeProperties((prevProps) => ({
                            ...prevProps,
                            mazeRows: Math.max(1, parseInt(e.target.value, 10) || 1),
                          }))
                        }
                        className="number-input"
                      />
                    </div>

                    <div className="custom-grid-field">
                      <h3 className="property-heading">Columns</h3>
                      <input
                        type="number"
                        min="1"
                        value={shapeProperties.mazeColumns || 8}
                        onChange={(e) =>
                          setShapeProperties((prevProps) => ({
                            ...prevProps,
                            mazeColumns: Math.max(1, parseInt(e.target.value, 10) || 1),
                          }))
                        }
                        className="number-input"
                      />
                    </div>
                  </div>

                  <div className="custom-grid-row">
                    <div className="custom-grid-field">
                      <h3 className="property-heading">Exits</h3>
                      <input
                        type="number"
                        min="1"
                        max="4"
                        value={shapeProperties.mazeExits || 2}
                        onChange={(e) =>
                          setShapeProperties((prevProps) => ({
                            ...prevProps,
                            mazeExits: Math.min(4, Math.max(1, parseInt(e.target.value, 10) || 1)),
                          }))
                        }
                        className="number-input"
                      />
                    </div>

                    <div className="custom-grid-field">
                      <h3 className="property-heading">Complexity</h3>
                      <input
                        type="number"
                        min="1"
                        max="5"
                        value={shapeProperties.mazeComplexity || 3}
                        onChange={(e) =>
                          setShapeProperties((prevProps) => ({
                            ...prevProps,
                            mazeComplexity: Math.min(5, Math.max(1, parseInt(e.target.value, 10) || 1)),
                          }))
                        }
                        className="number-input"
                      />
                    </div>
                  </div>
                </>
              )}
                   

              {/* Stroke Width Property */}
              <div className="property-group">
                <h3 className="property-heading">Stroke Width:</h3>
                <input
                  type="number"
                  value={shapeProperties.strokeWidth}
                  onChange={handleShapeStrokeWidth}
                  className="number-input"
                />
              </div>

              {/* Stroke Color with Checkbox */}
              <div className="property-group">
                <h3 className="property-heading">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={shapeProperties.strokeEnabled}
                      onChange={(e) =>
                        setShapeProperties((prevProps) => ({
                          ...prevProps,
                          strokeEnabled: e.target.checked,
                        }))
                      }
                    />
                    Stroke Color:
                  </label>
                </h3>
                <input
                  type="color"
                  value={shapeProperties.strokeColor || '#000000'}
                  onFocus={pushToUndoStack}
                  onChange={handleShapeStrokeColorChange}
                  className="color-input"
                  disabled={!shapeProperties.strokeEnabled}
                />
                {allStrokeEnabled &&
                  renderColourSwatches(shapeProperties.strokeColor || '#000000', (colour) => {
                    handleShapeStrokeColorChange({ target: { value: colour } });
                  })}
              </div>

              {/* Fill Color with Checkbox */}
              <div className="property-group">
                <h3 className="property-heading">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={shapeProperties.fillEnabled}
                      onChange={(e) =>
                        setShapeProperties((prevProps) => ({
                          ...prevProps,
                          fillEnabled: e.target.checked,
                        }))
                      }
                    />
                    Fill Color:
                  </label>
                </h3>
                <input
                  type="color"
                  value={shapeProperties.fillColor || '#FFFFFF'}
                  onFocus={pushToUndoStack}
                  onChange={handleShapeFillColorChange}
                  className="color-input"
                  disabled={!shapeProperties.fillEnabled}
                />
                {allFillEnabled &&
                  renderColourSwatches(shapeProperties.fillColor || '#ffffff', (colour) => {
                    handleShapeFillColorChange({ target: { value: colour } });
                  })}
              </div>


              {renderFillStyleControls()}


              {/* Opacity Slider */}
              <div className="property-group">
                <h3 className="property-heading">Opacity:</h3>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={shapeProperties.opacity}
                  onChange={handleShapeOpacityChange}
                  className="opacity-slider"
                  onMouseUp={handleShapeOpacityChange} // Push to Undo stack on mouse up
                />
              </div>

              {selectedShapeType === 'rect'
                ? renderShapeStrokeStyleControls()
                : renderBasicShapeDashControls()}
                
            </div>
          ) : selectedTool === 'line' ? (
            <div className="line-tool">
              {/* Stroke Width Property */}
              <div className="property-group">
                <h3 className="property-heading">Stroke Width:</h3>
                <input
                  type="number"
                  value={lineProperties.strokeWidth}
                  onChange={(e) => updateLinePropertiesHandler(e, 'strokeWidth')}
                  className="number-input"
                />
              </div>

              {/* Stroke Color Property */}
              <div className="property-group">
                <h3 className="property-heading">Stroke Color:</h3>
                <input
                  type="color"
                  value={lineProperties.strokeColor}
                  onFocus={pushToUndoStack}
                  onChange={(e) => updateLinePropertiesHandler(e, 'strokeColor')}
                  className="color-input"
                />
                {renderColourSwatches(lineProperties.strokeColor || '#000000', (colour) => {
                  updateLinePropertiesHandler({ target: { value: colour } }, 'strokeColor');
                })}
              </div>

              {/* Line Opacity */}
                <div className="property-group">
                  <h3 className="property-heading">Opacity:</h3>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={lineProperties.opacity ?? 1}
                    onChange={(e) => updateLinePropertiesHandler(e, 'opacity')}
                    className="opacity-slider"
                  />
                </div>

              {/* Dashed Line Checkbox */}
              {renderDashToggleCard({
                checked: lineProperties.dashEnabled,
                onToggle: (e) => updateLinePropertiesHandler(e, 'dashEnabled'),
                dash: lineProperties.dash,
                onChangeDash: (nextDash) => {
                  updateLinePropertiesHandler(
                    { target: { value: nextDash.join(',') } },
                    'dash'
                  );
                },
              })}

              
              {/* Zigzag / Wavy Line Generator */}
              {/* Only show tracing line generator if NOT editing an existing one */}
              {selectedTool === 'line' && selectedShapeIds.length === 0 && (
                <div className="property-group tracing-line-controls">                 
                  <h3 className="property-heading">Insert Wave/Zigzag:</h3>
                  <button className="library-button" onClick={() => generateTracingLine('zigzag')}>
                    Insert Zigzag
                  </button>

                  <button className="library-button" onClick={() => generateTracingLine('wavy')}>
                    Insert Wave
                  </button>

                  <h3 className="property-heading">Points:</h3>

                  <input
                    type="number"
                    min="2"
                    max="100"
                    className="number-input"
                    value={lineProperties.tracingPoints || 5}
                    onChange={(e) =>
                      updateLinePropertiesHandler({ target: { value: parseInt(e.target.value) } }, 'tracingPoints')
                    }
                  />

                  <h3 className="property-heading">Length:</h3>
                  <input
                    type="number"
                    min="10"
                    max="1000"
                    className="number-input"
                    value={lineProperties.tracingLength || 300}
                    onChange={(e) =>
                      updateLinePropertiesHandler({ target: { value: parseInt(e.target.value) } }, 'tracingLength')
                    }
                  />

                    <h3 className="property-heading">Height:</h3>
                      <input
                        type="number"
                        min="10"
                        max="500"
                        className="number-input"
                        value={lineProperties.tracingHeight || 50}
                        onChange={(e) =>
                          updateLinePropertiesHandler(
                            { target: { value: parseInt(e.target.value) } },
                            'tracingHeight'
                          )
                        }
                      />

                </div>
              )}


              {selectedShape?.meta?.tracingType && (
              <div className="property-group">
                <h3 className="property-heading">Edit Wave/Zigzag:</h3>

                <h3 className="property-heading">Points:</h3>

                <input
                  type="number"
                  min="2"
                  max="100"
                  className="number-input"
                  value={selectedShape.meta.tracingPoints}
                  onChange={(e) =>
                    regenerateTracingLine(
                      selectedShape,
                      selectedShape.meta.tracingType,
                      parseInt(e.target.value),
                      selectedShape.meta.tracingLength
                    )
                  }
                />

                <h3 className="property-heading">Length:</h3>
                <input
                  type="number"
                  min="10"
                  max="1000"
                  className="number-input"
                  value={selectedShape.meta.tracingLength}
                  onChange={(e) =>
                    regenerateTracingLine(
                      selectedShape,
                      selectedShape.meta.tracingType,
                      selectedShape.meta.tracingPoints,
                      parseInt(e.target.value)
                    )
                  }
                />

              <h3 className="property-heading">Height:</h3>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    className="number-input"
                    value={selectedShape.meta.tracingHeight || 50}
                    onChange={(e) =>
                      regenerateTracingLine(
                        selectedShape,
                        selectedShape.meta.tracingType,
                        selectedShape.meta.tracingPoints,
                        selectedShape.meta.tracingLength,
                        parseInt(e.target.value)
                      )
                    }
                  />
              </div>
            )}


            </div>
          ) : selectedTool === 'pencil' ? (
            <div className="pencil-tool">
              {/* Stroke Width Property */}
              <div className="property-group">
                <h3 className="property-heading">Stroke Width:</h3>
                <input
                  type="number"
                  value={pencilProperties.strokeWidth}
                  onChange={(e) => updatePencilPropertiesHandler(e, 'strokeWidth')}
                  className="number-input"
                />
              </div>   

              <div className="property-group">
                <h3 className="property-heading">Smoothness:</h3>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.25"
                  value={pencilProperties.tension}
                  onChange={(e) => updatePencilPropertiesHandler(e, 'tension')}
                  className="opacity-slider"
                />
              </div>     
              
          
              {/* Stroke Color Property */}
              <div className="property-group">
                <h3 className="property-heading">Stroke Color:</h3>
                <input
                  type="color"
                  value={pencilProperties.strokeColor}
                  onFocus={pushToUndoStack}
                  onChange={(e) => updatePencilPropertiesHandler(e, 'strokeColor')}
                  className="color-input"
                />
                {renderColourSwatches(pencilProperties.strokeColor || '#000000', (colour) => {
                  updatePencilPropertiesHandler({ target: { value: colour } }, 'strokeColor');
                })}
              </div>

              {/* Pencil Opacity */}
              <div className="property-group">
                <h3 className="property-heading">Opacity:</h3>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={pencilProperties.opacity}
                  onChange={(e) => updatePencilPropertiesHandler(e, 'opacity')}
                  className="opacity-slider"
                />
              </div>


          
              {/* Dashed Line Checkbox */}
              {renderDashToggleCard({
                checked: pencilProperties.dashEnabled,
                onToggle: (e) => {
                  const checked = e.target.checked;
                  const nextDash = checked
                    ? (pencilProperties.dash?.length ? pencilProperties.dash : [10, 5])
                    : [];

                  updatePencilPropertiesHandler(
                    { target: { value: nextDash.join(',') } },
                    'dash'
                  );

                  updatePencilPropertiesHandler(
                    { target: { checked } },
                    'dashEnabled'
                  );
                },
                dash: pencilProperties.dash,
                onChangeDash: (nextDash) => {
                  updatePencilPropertiesHandler(
                    { target: { value: nextDash.join(',') } },
                    'dash'
                  );
                },
              })}
            </div>
          
          ) : selectedTool === 'workspaceInfo' ? (
            <div className="workspace-orientation">
              {/* Canvas Orientation */}
              <div className="canvas-orientation">
                <h3>Canvas Orientation</h3>
                <div className="orientation-selection">
                  <button
                    onClick={() => changeCanvasOrientation('portrait')}
                    className={
                      pages[activePage].orientation === 'portrait'
                        ? 'active orientation-button'
                        : 'orientation-button'
                    }
                    aria-label="Portrait Orientation"
                  >
                    <svg
                      className="orientation-icon portrait"
                      viewBox="0 0 24 32"
                      aria-hidden="true"
                    >
                      <rect x="1.5" y="1.5" width="21" height="29" rx="2" />
                      <circle cx="17" cy="9" r="2.5" />
                      <path d="M3.5 25.5 9 18l4 4 3-3 4.5 6.5" />
                    </svg>
                    Portrait
                  </button>
                  <button
                    onClick={() => changeCanvasOrientation('landscape')}
                    className={
                      pages[activePage].orientation === 'landscape'
                        ? 'active orientation-button'
                        : 'orientation-button'
                    }
                    aria-label="Landscape Orientation"
                  >
                    <svg
                      className="orientation-icon landscape"
                      viewBox="0 0 32 24"
                      aria-hidden="true"
                    >
                      <rect x="1.5" y="1.5" width="29" height="21" rx="2" />
                      <circle cx="24" cy="8" r="2.5" />
                      <path d="M3.5 18.5 10 11l5 5 4-4 9.5 6.5" />
                    </svg>
                    Landscape
                  </button>
                </div>
              </div>

              {/* Grid Settings */}
                            {/* Grid Settings */}
              <div className="grid-settings">
                <h3>Grid Settings</h3>

                {/* Simple Grid */}
                <div className="property-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isGridVisible}
                      onChange={(e) => {
                        setIsGridVisible(e.target.checked);
                      }}
                    />
                    Simple Grid
                  </label>
                </div>

                {/* Quarters Grid */}
                <div className="property-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isQuartersGridVisible}
                      onChange={(e) => {
                        setIsQuartersGridVisible(e.target.checked);
                      }}
                    />
                    Quarters Grid
                  </label>
                </div>

                {/* Thirds Grid */}
                <div className="property-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isThirdsGridVisible}
                      onChange={(e) => {
                        setIsThirdsGridVisible(e.target.checked);
                      }}
                    />
                    Thirds Grid
                  </label>
                </div>

                {/* Custom Grid */}
                <div className="property-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isCustomGridVisible}
                      onChange={(e) => {
                        setIsCustomGridVisible(e.target.checked);
                      }}
                    />
                    Custom Grid
                  </label>
                </div>

                {isCustomGridVisible && (
                  <div className="custom-grid-row">
                    <div className="custom-grid-field">
                      <h3 className="property-heading">Rows</h3>

                      <input
                        type="number"
                        min="1"
                        value={customGridSettings.horizontalSections}
                        onChange={(e) => {
                          const value = Math.max(
                            1,
                            parseInt(e.target.value, 10) || 1
                          );

                          setCustomGridSettings((prev) => ({
                            ...prev,
                            horizontalSections: value,
                          }));
                        }}
                        className="number-input"
                      />
                    </div>

                    <div className="custom-grid-field">
                      <h3 className="property-heading">Columns</h3>

                      <input
                        type="number"
                        min="1"
                        value={customGridSettings.verticalSections}
                        onChange={(e) => {
                          const value = Math.max(
                            1,
                            parseInt(e.target.value, 10) || 1
                          );

                          setCustomGridSettings((prev) => ({
                            ...prev,
                            verticalSections: value,
                          }));
                        }}
                        className="number-input"
                      />
                    </div>
                  </div>
                )}

                {/* Original full-page Ruler Grid */}
                <div className="property-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isRulerGridVisible}
                      onChange={(e) => {
                        setIsRulerGridVisible(e.target.checked);
                      }}
                    />
                    Ruler Grid (cm + mm)
                  </label>
                </div>

                {/* New movable Measurement Ruler */}
                <div className="property-group">
                  <h3 className="property-heading">Measurement Ruler</h3>

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isMeasurementRulerVisible}
                      onChange={(e) => {
                        const checked = e.target.checked;

                        setIsMeasurementRulerVisible(checked);

                        if (checked) {
                          setSelectedShapeIds([]);
                          setIsMeasurementRulerSelected(true);
                          setSelectedTool('select');
                        } else {
                          setIsMeasurementRulerSelected(false);
                        }
                      }}
                    />
                    Show 30 cm Ruler
                  </label>

                  {isMeasurementRulerVisible && (
                    <>
                      <div style={{ marginTop: '12px' }}>
                        <h3 className="property-heading">Units:</h3>

                        <select
                          value={measurementRulerSettings.unit}
                          onChange={(e) => {
                            setMeasurementRulerSettings((prev) => ({
                              ...prev,
                              unit: e.target.value,
                            }));
                          }}
                          className="select-input"
                        >
                          <option value="cm">
                            Centimetres and millimetres
                          </option>

                          <option value="in">
                            Inches and eighths
                          </option>
                        </select>
                      </div>

                      <div style={{ marginTop: '12px' }}>
                        <h3 className="property-heading">Rotation:</h3>

                        <input
                          type="number"
                          min="-360"
                          max="360"
                          step="1"
                          value={Math.round(
                            measurementRulerSettings.rotation || 0
                          )}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);

                            setMeasurementRulerSettings((prev) => ({
                              ...prev,
                              rotation: Number.isFinite(value) ? value : 0,
                            }));
                          }}
                          className="number-input"
                        />
                      </div>

                      <button
                        type="button"
                        className="library-button"
                        style={{ marginTop: '12px' }}
                        onClick={() => {
                          setMeasurementRulerSettings((prev) => ({
                            ...prev,
                            x: pages[activePage].width / 2,
                            y: 120,
                            rotation: 0,
                          }));
                        }}
                      >
                        Reset Ruler Position
                      </button>

                      <p
                        style={{
                          margin: '10px 0 0',
                          fontSize: '12px',
                          lineHeight: 1.4,
                          opacity: 0.7,
                        }}
                      >
                        Drag the ruler using the Select tool. Use the round
                        handle to rotate it. This visual ruler is not included
                        in exports.
                      </p>
                    </>
                  )}
                </div>
              </div>

              


              {/* Workspace Mood */}
              <div className="workspace-info">
                <h3>Workspace Mood</h3>
                <div className="property-group">
                  <h3 className="property-heading">Mood:</h3>
                  <select
                      value={localStorage.getItem('workspaceMood') || 'EDUGroove'}
                      onChange={(e) => changeMood(e.target.value)}
                      className="select-input"
                    >
                    <option value="EDUGroove">EDU Groove</option>
                    <option value="Crafty">Crafty</option>                    
                    <option value="Calm">Calm</option>
                    <option value="Energetic">Energetic</option>
                    <option value="Creative">Creative</option>                    
                    <option value="Workbench">Workbench</option>
                    <option value="Hungry">Hungry</option>
                    <option value="Cute">Cute</option>
                    <option value="Zen">Zen</option>
                    <option value="Default">Clean</option>
                  </select>
                </div>
              </div>
            </div>
          ) : selectedTool === 'zoom' ? (
            <div className="zoom-tool">
              <button className="add-page-button zoom-button" onClick={handleZoomIn} aria-label="Zoom In">
                <FontAwesomeIcon icon={faPlus} />
              </button>
              <button className="add-page-button zoom-button" onClick={handleZoomOut} aria-label="Zoom Out">
                <FontAwesomeIcon icon={faMinus} />
              </button>
            </div>
          ) : selectedTool === 'addPage' ? (
            <div className="add-page-tool">
              <button className="add-page-button" onClick={addBlankPage}>
                <FontAwesomeIcon icon={faPlus} /> Add Blank Page
              </button>
              <button className="add-page-button" onClick={duplicateCurrentPage}>
                <FontAwesomeIcon icon={faPlus} /> Duplicate Page
              </button>
            </div>
                    ) : selectedTool === 'bucket' ? (
                    <div className="bucket-tool">
                      <div className="property-group">
                        <h3 className="property-heading">Bucket Colour:</h3>
                        <input
                          type="color"
                          value={bucketSettings.color}
                          onChange={(e) =>
                            setBucketSettings((prev) => ({
                              ...prev,
                              color: e.target.value,
                            }))
                          }
                          className="color-input"
                        />
                      </div>

              <div className="property-group">
                <h3 className="property-heading">Affect:</h3>
                <select
                  value={bucketSettings.target}
                  onChange={(e) =>
                    setBucketSettings((prev) => ({
                      ...prev,
                      target: e.target.value,
                    }))
                  }
                  className="select-input"
                >
                  <option value="auto">Auto</option>
                  <option value="fill">Fill</option>
                  <option value="stroke">Stroke</option>
                  <option value="text">Text</option>
                </select>
              </div>

              <div className="property-group">
                <p style={{ margin: 0, fontSize: '12px', opacity: 0.7 }}>
                  Click any colour-changeable object on the canvas to apply this colour.
                </p>
              </div>
            </div>  
          ) : selectedTool ? (
            <div>
              {/* Additional tools can be added here */}
            </div>
          ) : (
            <div>
              {/* Default content when no tool is selected */}
            </div>
          )}

          {showTemplateTutorial && (
            <div className="property-group template-tutorial-property">
              <h3 className="property-heading">Activity Tutorial:</h3>

              <div
                className={`template-tutorial-inline-video ${
                  currentTemplate?.videoAspectRatio === '9:16'
                    ? 'portrait'
                    : 'landscape'
                }`}
              >
                <iframe
                  src={templateVideoInlineUrl}
                  title={`${currentTemplate?.name || 'Activity'} tutorial video`}
                  loading="lazy"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>

              <button
                type="button"
                className="library-button template-tutorial-large-button"
                onClick={() => setIsTemplateVideoOpen(true)}
              >
                Open Larger View
              </button>
            </div>
          )}

          {/* Community Section */}
          <div className="community-section">
          <img
            src={HeaderImage}
            alt="EDU Groove"
            className="tool-header-logo"
          />
        <div className="social-icons">
            <a href="https://www.facebook.com/profile.php?id=61569095273653" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <img src={FacebookIcon} alt="Facebook" className="social-icon" />
            </a>        
            <a href="https://www.youtube.com/@EDUGrooveHub" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
              <img src={YoutubeIcon} alt="YouTube" className="social-icon" />
            </a>
          </div>
      </div>
        </div>
      )}

      {isTemplateVideoOpen && templateVideoEmbedUrl && (
        <div
          className="template-video-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tool-template-video-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIsTemplateVideoOpen(false);
            }
          }}
        >
          <FocusTrap>
            <div
              className={`template-video-modal ${
                currentTemplate?.videoAspectRatio === '9:16'
                  ? 'portrait'
                  : 'landscape'
              }`}
            >
              <div className="template-video-header">
                <h2 id="tool-template-video-title">
                  {currentTemplate?.name || 'Activity tutorial'}
                </h2>
                <button
                  type="button"
                  className="templates-modal-close-btn template-video-close-btn"
                  onClick={() => setIsTemplateVideoOpen(false)}
                  aria-label="Close video"
                >
                  ×
                </button>
              </div>
              <div className="template-video-frame">
                <iframe
                  src={templateVideoEmbedUrl}
                  title={`${currentTemplate?.name || 'Activity'} tutorial video`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </FocusTrap>
        </div>
      )}
    </div>
  );
};



export default ToolPropertiesPanel;
